/**
 * API client for the Reprotec Work backend (Contracts Board + Time).
 * Base URL resolves to localhost:8080 in dev, same-origin in UAT/prod.
 *
 * Auth strategy: __session httpOnly cookie issued by the backend on login.
 * Browser carries it on every fetch via `credentials: 'include'`. No client-
 * side token storage; no Authorization header. (Phase 14-04 dropped the legacy
 * Bearer Google ID token fallback inherited from the pre-monorepo era.)
 */

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8080';

// In dev, the backend uses X-Dev-User to identify the caller (no real auth token needed).
const DEV_USER = import.meta.env.VITE_DEV_USER ?? 'super.user@reprotec.uk';

// 14-04 cleanup: drop pre-existing cb_id_token from any tab opened before the
// single-domain consolidation deploy. Backend Bearer middleware still accepts
// ID tokens for backward compat through the soft transition; this just stops
// the SPA from re-sending one. Removed in 14-05 once the soft window closes.
if (typeof sessionStorage !== 'undefined') {
  sessionStorage.removeItem('cb_id_token');
}

/**
 * Structured error thrown by `apiFetch` for any non-ok response.
 *
 * ARCH-4 (plan 50-09): previously the conflict endpoints (job equipment /
 * vehicles add, admin equipment update, materials confirm) hand-rolled their
 * own `fetch` — duplicating the dev-user header, the 401→/login redirect, and
 * the non-ok handling — purely so they could inspect the 409 body that
 * apiFetch's generic Error threw away. ApiHttpError carries `status` + the
 * raw text + the parsed JSON `body`, so those endpoints can now go through
 * apiFetch and map the 409 in a catch.
 *
 * `message` keeps the legacy `API <status>: <rawText>` shape so existing
 * message-based assertions (extractMutationErrorMessage etc.) are unaffected.
 */
export class ApiHttpError extends Error {
  readonly status: number;
  readonly rawText: string;
  readonly body: unknown;
  constructor(status: number, rawText: string) {
    super(`API ${status}: ${rawText}`);
    this.name = 'ApiHttpError';
    this.status = status;
    this.rawText = rawText;
    let parsed: unknown = undefined;
    try {
      parsed = rawText ? JSON.parse(rawText) : undefined;
    } catch {
      parsed = undefined;
    }
    this.body = parsed;
  }
}

// Exported (Phase 70) so the native Materials pages can own co-located, per-domain
// API clients (e.g. app/components/materials/orders/orders.api.ts) without each
// build colliding on this shared file. Goes through the same 401/dev-user/non-ok
// (ApiHttpError) contract as the centralized `api` object below.
export async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const isDev = import.meta.env.DEV;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options?.headers as Record<string, string>),
  };

  if (isDev || import.meta.env.VITE_DEV_USER) {
    headers['X-Dev-User'] = DEV_USER;
  }

  const res = await fetch(`${BASE_URL}${path}`, { ...options, headers, credentials: 'include' });

  if (res.status === 401 && window.location.pathname !== '/login') {
    window.location.href = '/login';
    throw new Error('Session expired');
  }

  if (!res.ok) {
    const body = await res.text();
    throw new ApiHttpError(res.status, body);
  }

  if (res.status === 204) {
    return undefined as T;
  }

  return res.json() as Promise<T>;
}

// ─── Response envelopes ───────────────────────────────────────────────────────

interface ListResponse<T> {
  data: T[];
}

interface SingleResponse<T> {
  data: T;
}

// ─── Raw API types (matching backend schema) ──────────────────────────────────

export interface ApiStaff {
  id: string;
  name: string;
  known_as: string | null;
  initials: string;
  email: string | null;
  work_email: string | null;
  is_subcontractor: boolean;
  staff_role: 'operative' | 'manager' | 'back_office';
  default_hours: number;
  // #234 Task 7: labour cost £/hr for job costing. NUMERIC(10,2) → pg serialises
  // as a string; nullable (blended-rate fallback). Finance/admin-managed.
  cost_rate: number | string | null;
  is_active: boolean;
  // 20-03: surfaced via SELECT s.* on /api/v1/staff (data already returned by
  // the existing query). Drives Staff & Subs page admin-only access controls.
  auth_role: 'none' | 'basic_access' | 'viewer' | 'planner' | 'admin' | 'superadmin';
  can_clock_in: boolean;
  xero_employee_id: string | null;
  company_id: string | null;
  company_name: string | null;
  active_assignment_count?: number;
  created_at: string;
  updated_at: string;
}

export interface ApiCompany {
  id: string;
  name: string;
  initials: string | null;
  is_internal: boolean;
  is_active: boolean;
  staff_count?: string;
  created_at: string;
  updated_at: string;
}

export interface ApiWorkItem {
  id: string;
  source: 'hubspot' | 'manual' | 'hubspot_ticket';
  source_portal_id?: string | null;
  source_id: string | null;
  ion: string | null;
  name: string;
  client_name: string | null;
  site_address: string | null;
  site_address_verified: boolean;
  source_status: string | null;
  status: 'draft' | 'available' | 'inactive' | 'scheduled' | 'completed' | 'cancelled' | 'archived';
  source_metadata: {
    suggested_start_date: string | null;
    suggested_end_date: string | null;
    amount: number | null;
    // Phase 41-02 (#65): ticket-source work_items carry the HubSpot
    // ticket.location enum here (G2 / HQ / Client Site Address /
    // Employees Home Address) — drives the Scheduling Wizard pre-flight
    // visibility decision (verification only runs for deals + tickets
    // whose location === 'Client Site Address').
    location?: string | null;
    [key: string]: unknown;
  } | null;
  // Phase 32-02 (Issue #34): linked-deal id for ticket-source work_items.
  // Null for deals (their source_id IS the deal id) and manual entries.
  linked_deal_id?: string | null;
  // Phase 41-02 (#65): linked deal's site_address_verified flag mirrored
  // from HubSpot at ticketSync time. Used by Scheduling Wizard pre-flight
  // to skip verification on tickets whose linked deal is already verified.
  linked_deal_site_address_verified?: boolean;
  // Phase 32-05: the linked deal's own site_address (the customer's
  // location), distinct from `site_address` which is the resolved worksite
  // (G2 address for G2 tickets, etc.). Used as the seed address for the
  // verification pre-flight on Client Site Address tickets.
  linked_deal_site_address?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ApiJob {
  id: string;
  work_item_id: string;
  // Phase 23-02: projected from work_items.source via the existing JOIN.
  // Drives source-aware UI gating (e.g. phase status options in JobDetailPanel).
  source: 'hubspot' | 'manual' | 'hubspot_ticket';
  source_portal_id?: string | null;
  ion: string;
  name: string;
  client_name: string;
  company_domain: string | null;
  site_address: string | null;
  site_address_verified?: boolean;
  // Phase 39-01: parent-level lifecycle only (draft/in_progress/archived
  // removed by migration 061 step 7). Phase-level status lives on
  // ApiJobPhase.status with its own 5-value enum.
  status: 'scheduled' | 'completed' | 'cancelled' | 'postponed';
  // Phase 39-01: start_date/end_date/notes/contracts_manager_id/status/
  // daily_scopes now live on job_phases (per spec Q3). Backend GET endpoints
  // (routes/jobs.ts + routes/board.ts) JOIN through job_phases[1] and project
  // these fields into the response shape so single-phase callers see the
  // pre-migration wire shape unchanged. Multi-phase consumers (39-03+) MUST
  // switch to per-phase endpoints rather than relying on Phase 1 projection.
  start_date: string;
  end_date: string | null;
  drive_folder_id: string | null;
  notes: string | null;
  daily_scopes: Record<string, string>;
  contracts_manager_id?: string | null;
  // Phase 39-04: per-phase fields projected on board responses (each
  // data.jobs[] element is one (phase, date) row). Optional because legacy
  // /jobs/:id callers still get the Phase 1 projection without these explicit
  // phase identifiers (they were aliased into start_date/end_date/etc). Board
  // consumers (Phase 39-05 ContractsBoard/JobCard) MUST check presence at the
  // consumption site or assert via local narrowing.
  phase_id?: string;
  phase_number?: number;
  phase_status?: 'provisional' | 'confirmed' | 'in_progress' | 'cancelled' | 'completed';
  cancelled_at?: string | null;
  cancelled_reason?: string | null;
  // Phase 39-07 follow-up: explicit per-phase aliases. The board SELECT
  // previously had `j.*, jp.contracts_manager_id, jp.status, ...`
  // which produced duplicate-column-named rows; the `pg` driver kept the
  // first (jobs-level) value and silently dropped the per-phase value. The
  // board now aliases each conflicting jp.* column with a phase_-prefix
  // and adaptBoardPhaseRow overlays them onto the base Job projection.
  phase_start_date?: string;
  phase_end_date?: string | null;
  phase_contracts_manager_id?: string | null;
  phase_notes?: string | null;
  phase_daily_scopes?: Record<string, unknown>;
  // Phase 39-05 Spec [2] amendment (2026-05-22): total phase count on the
  // parent job (all statuses; cancelled included). Drives the JobCard
  // "Phase N of M" badge denominator so M is stable across windows —
  // single-phase legacy jobs get phase_count_total=1 → badge hidden.
  phase_count_total?: number;
  // Phase 32-02 (Issue #34 sub-item 1): linked-deal metadata for ticket-sourced
  // jobs. All 4 NULL when the underlying ticket has no associated deal.
  linked_deal_id?: string | null;
  linked_deal_ion?: string | null;
  linked_deal_name?: string | null;
  linked_deal_client?: string | null;
  // Phase 32-05: the linked deal's own site_address (the customer's location).
  // Distinct from `site_address` which is the resolved worksite for THIS ticket.
  // Surfaced in the JobDetailPanel Linked Deal popover so planners see the
  // deal's address even when the ticket location enum is G2 / HQ / Employees
  // Home Address (the two diverge except for location='Client Site Address').
  // Null when the ticket has no linked deal OR when the deal has no
  // site_address property set in HubSpot.
  linked_deal_site_address?: string | null;
  // Phase 32-04 (Issue #34 sub-item 2): raw HubSpot ticket.location enum value
  // (G2 / HQ / Client Site Address / Employees Home Address). Null for
  // deal-source and manual jobs. Drives the source-label chip in JobDetailPanel.
  location_source?: string | null;
  // Per-phase profitability: the linked HubSpot deal `amount` (deal value),
  // surfaced on job reads to drive the job-level revenue / profitability view.
  // Null for manual/ticket jobs with no deal amount. NUMERIC-as-money is small
  // enough to carry as a JS number here (matches source_metadata.amount).
  hubspot_amount: number | null;
  created_at: string;
  updated_at: string;
}

// Phase 39-01: per-phase scheduling row. Every job has ≥1 phase (legacy
// jobs backfill to Phase 1). Multi-phase support lands in 39-03+ — until
// then, all consumers can rely on the projected Phase 1 fields on ApiJob.
export interface ApiJobPhase {
  id: string;
  job_id: string;
  phase_number: number;
  start_date: string;
  end_date: string;
  contracts_manager_id: string | null;
  notes: string | null;
  status: 'provisional' | 'confirmed' | 'in_progress' | 'cancelled' | 'completed';
  daily_scopes: Record<string, string>;
  cancelled_at: string | null;
  cancelled_reason: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Phase 39-03: per-phase input shape for the multi-phase scheduling wizard.
 * Mirrors the backend `phaseInputSchema` on POST /work-items/:id/schedule
 * (routes/work-items.ts). phase_number is explicit so the planner can skip
 * Phase 1 via wizard delete; gap preserved as Q9 audit signal.
 */
// Phase 41-02 (#65): Google Geocoding match returned by the verify endpoint
// + accepted by the confirm endpoint as the planner's picked address.
export interface ApiGeocodeMatch {
  placeId: string;
  formattedAddress: string;
  lat: number;
  lng: number;
}

export interface ApiWorkItemSchedulePhaseInput {
  phase_number: number;
  start_date: string;
  end_date: string;
  contracts_manager_id?: string | null;
  status?: 'provisional' | 'confirmed';
  notes?: string | null;
  daily_scopes?: Record<string, string>;
  // Per-phase profitability: optional planner-set budget for this phase.
  // NUMERIC over the wire → string; null/omitted leaves it unset.
  phase_budget?: string | null;
}

export interface ApiAssignment {
  id: string;
  job_id: string;
  // Phase 39-07: phase_id has been on assignment_segments since Migration 061;
  // surfacing in the FE so JobDetailPanel can filter assignedStaff by selected
  // phase. Optional defensively in case some legacy code paths return rows
  // without the column.
  phase_id?: string;
  staff_id: string;
  date: string;
  start_time: string;
  end_time: string;
  is_overtime: boolean;
  is_team_leader: boolean;
  role: string | null;
  notes: string | null;
}

export interface ApiLeaveEntry {
  id: string;
  staff_id: string;
  date: string;
  leave_type: 'holiday' | 'sick' | 'compassionate' | 'unpaid' | 'bank_holiday' | 'unavailable';
  source: 'manual' | 'xero';
  notes?: string | null;
}

// Issue #255 (Facet 1): free-interval availability for a single date.
export interface ApiDayAvailability {
  date: string;
  /** A free slot of at least the requested duration exists on this day. */
  available: boolean;
  /** Staff member is on leave — the whole day is blocked. */
  onLeave: boolean;
  /** Free wall-clock gaps within the Working Day span ("HH:MM"). */
  freeIntervals: { start: string; end: string }[];
  /** Largest free gap — a sensible non-overlapping default to seed. */
  suggested: { start: string; end: string } | null;
  /** Existing non-released segments on the date (for the conflict label). */
  busy: {
    id: string;
    job_id: string;
    start_time: string;
    end_time: string;
    ion: string;
    job_name: string;
    phase_id: string | null;
    phase_number: number | null;
  }[];
}

// Issue #255 (Facet 2): a WTR pre-check block, with enough detail for the assign
// dialog to render a specific reason ("11h rest gap to the DD/MM shift") and a
// computed "More details" explainer — no AI call.
export interface WtrOffendingShift {
  id: string;
  /** YYYY-MM-DD of the clashing existing shift. */
  date: string;
  /** "HH:MM". */
  startTime: string;
  /** "HH:MM". */
  endTime: string;
  ion: string | null;
  jobName: string | null;
}

export interface WtrBlockDetail {
  type: 'daily_rest_violation' | 'weekly_rest_violation';
  /** Backend human-readable reason (fallback text). */
  reason: string;
  /** Hours of rest the proposal would actually leave. */
  measuredRestGapHours: number | null;
  /** Statutory requirement: 11 (daily) or 48 (weekly). */
  requiredRestHours: number;
  /** YYYY-MM-DD — earlier of the two adjacent dates. */
  windowStart: string;
  /** YYYY-MM-DD — later of the two adjacent dates. */
  windowEnd: string;
  /** The existing shift(s) the proposal clashes with, resolved to real times. */
  offendingShifts: WtrOffendingShift[];
}

// Board endpoints return joined rows with extra fields
export interface ApiBoardSegment extends ApiAssignment {
  staff_name: string;
  initials: string;
  ion: string;
  job_name: string;
}

export interface ApiBoardLeaveEntry extends ApiLeaveEntry {
  staff_name: string;
  initials: string;
}

// Phase 37-01 (Issue #51) + Phase 39-04: per-(phase, date) vehicle
// assignments projected alongside segments + leave for the JobCard middle-
// section registration pills. phase_id added in 39-04 so 39-05 can re-key
// the FE vehiclesMap from `${job_id}:${date}` to `${phase_id}:${date}` —
// multi-phase jobs with distinct vehicles per phase resolve correctly.
// job_id retained for FE cross-ref (BoardFilterBar still filters by job).
export interface ApiBoardVehicleAssignment {
  job_id: string;
  phase_id: string;
  date: string; // YYYY-MM-DD
  registrations: string[];
}

// ─── Phase 47 (GH #104): whole-phase reassignment analysis ──────────────────
//
// Shape returned by POST /jobs/:id/reschedule-preview (dry-run) and echoed
// back under `reassignment` on PATCH /jobs/:id when dateChangeStrategy is
// 'transfer' | 'clear'. Mirrors backend services/reassignment.ts field-by-
// field. When a phase's dates move, staff + vehicles + equipment move together
// ("transfer" non-conflicting, or "clear" all); conflicts enumerate what
// could NOT move and why, gaps flag dates left short-handed.
export type ReassignmentStrategy = 'transfer' | 'clear';

export type StaffConflictReason =
  | 'double_booked'
  | 'leave'
  | 'out_of_range'
  | 'daily_rest_violation'
  | 'weekly_rest_violation';
export type VehicleConflictReason = 'double_booked' | 'out_of_range' | 'unavailable';
export type EquipmentConflictReason = 'over_capacity' | 'out_of_range';

export interface ReassignmentStaffConflict {
  id: string;
  date: string; // YYYY-MM-DD
  reason: StaffConflictReason;
  staff_name: string;
  detail?: string;
  // Present only when reason === 'double_booked'.
  conflicting_ion?: string | null;
  conflicting_job_name?: string | null;
}

export interface ReassignmentVehicleConflict {
  id: string;
  date: string;
  reason: VehicleConflictReason;
  registration: string;
  unavailability_reason?: string;
  conflicting_ion?: string | null;
  conflicting_job_name?: string | null;
}

export interface ReassignmentEquipmentConflict {
  id: string;
  date: string;
  reason: EquipmentConflictReason;
  equipment_name: string;
}

export interface ReassignmentGap {
  date: string;
  missing_staff: number;
  missing_vehicles: number;
  missing_equipment: number;
}

export interface ReassignmentAnalysis {
  // newStart − oldStart, in days (signed).
  delta_days: number;
  // On the dry-run preview: counts that WOULD transfer cleanly. On the PATCH
  // echo: counts that ACTUALLY transferred.
  moved: { staff: number; vehicles: number; equipment: number };
  conflicts: {
    staff: ReassignmentStaffConflict[];
    vehicles: ReassignmentVehicleConflict[];
    equipment: ReassignmentEquipmentConflict[];
  };
  gaps: ReassignmentGap[];
}

// Phase 39-05 follow-up (2026-05-25, Migration 065): postponement snapshots.
// Phase 39-07: per-phase row shape returned by GET /jobs/:jobId/phases and
// the POST/PATCH/cancel endpoints. Mirrors backend services/phases.ts
// JobPhase interface field-by-field. Used by JobDetailPanel's phase
// navigator (39-07) + per-phase resource sections (39-08).
export interface ApiPhase {
  id: string;
  job_id: string;
  phase_number: number;
  status: 'provisional' | 'confirmed' | 'in_progress' | 'cancelled' | 'completed';
  start_date: string; // YYYY-MM-DD
  end_date: string; // YYYY-MM-DD (BE returns ::text always)
  contracts_manager_id: string | null;
  notes: string | null;
  daily_scopes: Record<string, unknown>;
  cancelled_at: string | null;
  cancelled_reason: string | null;
  // Phase 47-01 (GH #67): planner-controlled operative-visibility flag, read by
  // JobDetailPanel's per-phase "Viewable by operatives" checkbox.
  viewable_by_operatives: boolean;
  // Per-phase profitability: planner-set budget for this phase. NUMERIC →
  // pg serialises as a string (matches the wire shape the BE returns); null
  // when no budget has been set. Coerce at the consumption site for arithmetic.
  phase_budget: string | null;
  created_at: string;
  updated_at: string;
}

// POST /jobs/:jobId/phases body — phase_number is server-resolved (MAX+1
// per Q9 stable numbering; not a caller input).
export interface PhaseCreateBody {
  start_date: string;
  end_date: string;
  contracts_manager_id?: string | null;
  status?: 'provisional' | 'confirmed';
  notes?: string | null;
  daily_scopes?: Record<string, unknown>;
  // Per-phase profitability: optional planner-set budget. NUMERIC → string;
  // PhaseUpdateBody (Partial<>) inherits it so PATCH can edit the budget too.
  phase_budget?: string | null;
}

export type PhaseUpdateBody = Partial<PhaseCreateBody>;

export interface PhaseCancelBody {
  reason: string;
}

// Per-phase profitability: one row per phase returned by
// GET /api/v1/jobs/:id/phase-costs. Mirrors the backend PhaseCostRow shape
// field-for-field. Cost/hours metrics are summed server-side and arrive as JS
// numbers; phase_budget / gross_profit / margin_pct are nullable (no budget set
// or non-chargeable phase). labour_cost_estimated flags rows whose labour cost
// fell back to a blended rate (missing per-staff cost_rate).
export interface PhaseCost {
  phase_id: string | null;
  // null on the unphased (job-level) row — backend returns number | null.
  phase_number: number | null;
  phase_detail: string | null;
  chargeable: boolean;
  phase_budget: number | null;
  material_cost: number;
  labour_hours: number;
  labour_cost: number;
  actual_labour_hours: number;
  actual_labour_cost: number;
  labour_cost_estimated: boolean;
  total_cost: number;
  gross_profit: number | null;
  margin_pct: number | null;
}

// Self-contained — no JOIN to live jobs. Each row = one phase preserved at
// postpone-time. Multi-postpone of the same job accumulates as history.
// FE adapts each into a synthetic Job with phase_id = postponement.id and
// status = 'postponed' so JobCard's parent-status tombstone branch wins.
export interface ApiBoardPostponement {
  id: string;
  original_job_id: string;
  ion: string;
  name: string;
  client_name: string | null;
  source: 'hubspot' | 'manual' | 'hubspot_ticket' | null;
  work_item_id: string | null;
  phase_number: number;
  start_date: string; // YYYY-MM-DD
  end_date: string; // YYYY-MM-DD
  postponed_at: string;
  postponed_by: string | null;
  postponed_reason: string | null;
}

export interface ApiBoardMonth {
  month: number;
  year: number;
  jobs: ApiJob[];
  postponements: ApiBoardPostponement[];
  segments: ApiBoardSegment[];
  leave: ApiBoardLeaveEntry[];
  staff: ApiStaff[];
  vehicles: ApiBoardVehicleAssignment[];
}

export interface ApiBoardWeek {
  weekStart: string;
  weekEnd: string;
  jobs: ApiJob[];
  postponements: ApiBoardPostponement[];
  segments: ApiBoardSegment[];
  leave: ApiBoardLeaveEntry[];
  staff: ApiStaff[];
  vehicles: ApiBoardVehicleAssignment[];
}

export interface ApiBoardDay {
  date: string;
  jobs: ApiJob[];
  postponements: ApiBoardPostponement[];
  segments: ApiBoardSegment[];
  leave: ApiBoardLeaveEntry[];
  vehicles: ApiBoardVehicleAssignment[];
}

// Snapshots
export interface ApiSnapshot {
  id: string;
  snapshot_date: string;
  month: number;
  year: number;
  trigger: 'scheduled' | 'manual';
  captured_at: string;
}

// Issue #178: go-forward resource capture. These keys were added to the
// snapshot `data` JSONB in scheduling.ts so historic snapshots can faithfully
// reconstruct the board. They are OPTIONAL — snapshots taken before the
// extension shipped lack them entirely; the SnapshotViewer degrades gracefully.
export interface ApiSnapshotEquipmentAssignment {
  job_id: string;
  phase_id: string | null;
  equipment_item_id: string;
  date: string;
  quantity: number;
  identifier: string;
  item_notes: string | null;
  item_quantity: number | null;
  type_name: string;
}
export interface ApiSnapshotMaterial {
  id: string;
  job_id: string;
  phase_id: string | null;
  name: string;
  quantity: number | null;
  unit: string | null;
  cost_each?: number | null;
  notes: string | null;
}
export interface ApiSnapshotRequiredSkill {
  job_id: string;
  phase_id: string | null;
  id: string;
  name: string;
  description: string | null;
}

export interface ApiSnapshotDetail extends ApiSnapshot {
  data: {
    snapshotDate: string;
    month: number;
    year: number;
    capturedAt: string;
    jobs: ApiJob[];
    segments: (ApiBoardSegment & { is_subcontractor?: boolean; staff_role?: string })[];
    leave: ApiBoardLeaveEntry[];
    staff: (ApiStaff & { known_as?: string })[];
    // Issue #178 — optional (absent on pre-extension snapshots).
    vehicles?: ApiBoardVehicleAssignment[];
    equipment?: ApiSnapshotEquipmentAssignment[];
    materials?: ApiSnapshotMaterial[];
    requiredSkills?: ApiSnapshotRequiredSkill[];
  };
}

export type PlanningRestoreAction =
  | 'insert'
  | 'update'
  | 'release'
  | 'remove'
  | 'reserve'
  | 'recreate';
export type PlanningRestoreCounts = Record<PlanningRestoreAction, number>;
export interface ApiPlanningRestoreOperation {
  collection:
    | 'jobs'
    | 'phases'
    | 'segments'
    | 'vehicles'
    | 'equipment'
    | 'materials'
    | 'requiredSkills'
    | 'materialAllocations';
  id: string;
  action: PlanningRestoreAction;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
}
export interface ApiPlanningRestorePreview {
  version: 1;
  snapshotDigest: string;
  currentDigest: string;
  digest: string;
  operations: ApiPlanningRestoreOperation[];
  preserved: { collection: string; id: string; reason: string }[];
  conflicts: { code: string; entity: string; message: string }[];
  warnings: { code: string; entity: string; message: string }[];
  counts: PlanningRestoreCounts;
  workItemTransitions: { id: string; before: string; after: string }[];
  labels?: Record<string, string>;
  hasConflicts: boolean;
}
export interface ApiPlanningRestoreRequestSummary {
  id: string;
  snapshot_ref: string;
  requester_staff_id: string;
  requester_name?: string | null;
  reason: string;
  created_at: string;
  expires_at: string;
  status: 'pending' | 'completed' | 'stale' | 'expired' | 'rejected';
  authorizer_staff_id: string | null;
  authorizer_name?: string | null;
  decided_at: string | null;
  counts: PlanningRestoreCounts;
}
export interface ApiPlanningRestoreRequest extends Omit<
  ApiPlanningRestoreRequestSummary,
  'counts'
> {
  preview: ApiPlanningRestorePreview;
  result: {
    counts: PlanningRestoreCounts;
    workItemTransitions: ApiPlanningRestorePreview['workItemTransitions'];
  } | null;
}
export interface ApiPlanningRestoreRequestInput {
  snapshotId: string;
  retryKey: string;
  reason: string;
  reviewedDigest: string;
}

// Documents (Google Drive via backend)
export interface ApiDocument {
  id: string;
  name: string;
  mimeType: string;
  category:
    | 'rams'
    | 'method_statement'
    | 'drawing'
    | 'photo'
    | 'certificate'
    | 'spreadsheet'
    | 'folder'
    | 'other';
  webViewLink: string;
  downloadUrl?: string;
  modifiedTime: string;
  size?: string;
  iconLink?: string;
  folderPath?: string;
}

// Vehicles (admin) — Phase 34-01 backend, 34-02 frontend
export interface ApiVehicle {
  id: string;
  registration: string;
  make: string;
  model: string;
  is_active: boolean;
  // From the LEFT JOIN COUNT projection on GET /vehicles. Stable shape; drives
  // the per-row unavailable-dates badge + the delete-vs-archive guard in the UI.
  unavailability_count?: number;
  created_at: string;
  updated_at: string;
}

export interface ApiVehicleUnavailability {
  id: string;
  vehicle_id: string;
  date: string; // YYYY-MM-DD
  reason: VehicleUnavailabilityReason;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

// Mirrors the backend VEHICLE_UNAVAILABILITY_REASONS export in
// `apps/contracts-board/backend/src/routes/vehicles.ts`. The DB CHECK on
// `vehicle_unavailability.reason` is the integrity backstop; this TS const is
// the UI surface (dropdown source of truth). Keep in sync.
export const VEHICLE_UNAVAILABILITY_REASONS = [
  'service',
  'mot',
  'garage_repairs',
  'tyre_replacement',
  'other',
] as const;
export type VehicleUnavailabilityReason = (typeof VEHICLE_UNAVAILABILITY_REASONS)[number];

export const REASON_LABELS: Record<VehicleUnavailabilityReason, string> = {
  service: 'Service',
  mot: 'MOT',
  garage_repairs: 'Garage – Repairs',
  tyre_replacement: 'Tyre Replacement',
  other: 'Other',
};

// Skills
export interface ApiSkill {
  id: string;
  name: string;
  description: string | null;
  staff_count?: number;
  job_count?: number;
}

// Staff skill assignment (staff-level — links catalogue skills to a staff member)
export interface ApiStaffSkill {
  skill_id: string;
  skill_name: string;
  level: 'trainee' | 'competent' | 'supervisor' | 'assessor' | null;
}

// Materials (job-level)
export interface ApiMaterial {
  id: string;
  job_id: string;
  product_catalogue_id: string | null;
  name: string;
  quantity: number | null;
  unit: string | null;
  supplier: string | null;
  cost_each: number | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

// Job-vehicle assignment (per-day; mirrors ApiJobEquipment shape).
// Phase 34-03 — Issue #36 final piece.
export interface ApiJobVehicle {
  assignment_id: string;
  vehicle_id: string;
  registration: string;
  make: string;
  model: string;
  date: string; // YYYY-MM-DD
  // Populated when the assignment's date overlaps an unavailability entry
  // for this vehicle. Drives the soft-warn badge in the UI.
  unavailability_reason: VehicleUnavailabilityReason | null;
}

// 409 conflict body returned by POST /jobs/:id/vehicles when any of the
// requested dates collide with an existing assignment for this vehicle.
export interface AddVehiclesConflictDetails {
  unavailable_dates?: Array<{ date: string; reason: string }>;
  conflicts: Array<{
    date: string;
    conflictingJob: { id: string; ion: string; name: string };
  }>;
}

// Thin wrapper error so the UI can `try/catch` and inspect `conflicts`
// without parsing the error string.
export class AddVehiclesConflictError extends Error {
  constructor(public readonly conflicts: AddVehiclesConflictDetails['conflicts']) {
    super('vehicle assignment conflict');
    this.name = 'AddVehiclesConflictError';
  }
}

// Equipment assignment (job-level — links catalogue items to a job for a specific date)
export interface ApiJobEquipment {
  equipment_item_id: string;
  date: string;
  // Phase 36-01 (Issue #37): quantity tracking. quantity = how many of this
  // item booked for this (job, date). item_quantity = catalogue capacity
  // (NULL when N/A — unlimited / not tracked). available_quantity_for_date
  // = item_quantity − SUM(all allocations across all jobs for this date);
  // NULL when item is N/A; NEGATIVE when over-allocated (UI soft-warn).
  quantity: number;
  item_quantity: number | null;
  available_quantity_for_date: number | null;
  identifier: string;
  item_notes: string | null;
  type_name: string;
}

// Equipment (admin)
export interface ApiEquipmentItem {
  id: string;
  identifier: string;
  is_available: boolean;
  notes: string | null;
  // Phase 36-01 (Issue #37): catalogue capacity. NULL = N/A (unlimited /
  // not tracked); 1..50 = explicit cap. Admin sets via the +Add form or
  // inline-edit dropdown on the EquipmentTab.
  quantity: number | null;
  equipment_type_id: string;
  type_name: string;
}

// Phase 36-01 (Issue #37): structured 409 surfaced when admin tries to
// reduce equipment_items.quantity below the current per-date max allocation
// (Q3=b hard-block). UI surfaces the inline message + reverts the optimistic
// update. Mirrors AddVehiclesConflictError pattern from 34-03.
export interface ReduceEquipmentQuantityConflictDetails {
  current_max_allocation: number;
  conflicting_dates: string[];
}

export class ReduceEquipmentQuantityConflictError extends Error {
  readonly details: ReduceEquipmentQuantityConflictDetails;
  constructor(message: string, details: ReduceEquipmentQuantityConflictDetails) {
    super(message);
    this.name = 'ReduceEquipmentQuantityConflictError';
    this.details = details;
  }
}

// Phase 36-02 (Issue #37): structured 409 surfaced when planner tries to
// bulk-add equipment to date(s) that already have an assignment for this
// (job, item) tuple. Backend returns `details.conflicting_dates: string[]`;
// UI lists every offending date inline + keeps the form open.
export interface AddEquipmentConflictDetails {
  conflicting_dates: string[];
}

export class AddEquipmentConflictError extends Error {
  readonly conflicting_dates: string[];
  constructor(conflicting_dates: string[]) {
    super('equipment assignment conflict');
    this.name = 'AddEquipmentConflictError';
    this.conflicting_dates = conflicting_dates;
  }
}

// Equipment types (admin)
export interface ApiEquipmentType {
  id: string;
  name: string;
  description: string | null;
}

// Materials subsystem (Plan 04-01 backend; Plan 04-02 consumes from JobDetailPanel review UI)
// Shapes mirror apps/materials/backend/src/routes/{suppliers,materials-catalog,extracted-items}.ts.
// Duplicated here per the existing FE convention — no cross-workspace import.
// ─── Phase 78-01: job-materials allocation read contract (§4) ──────────────────
// Mirrors `GET /api/v1/materials/work-items/:workItemId/allocations` (Materials
// zone, mounted in cb-backend). Per-product, per-source breakdown with lifecycle
// status. jobs return phases[]; tickets return products[] directly (no phase
// layer). Built against the MOCKED contract until Materials ships Slice A — when
// the live endpoint returns real data there is no FE change.
// See docs/superpowers/specs/2026-06-21-job-materials-allocation-design.md §4.
export interface ApiAllocSource {
  // Phase 78-02: the per-source allocation row id, so the panel can target a
  // release (DELETE …/allocations/:id). The §4 read API must return this per
  // source — recorded in 78-02-PLAN as a contract point for Materials to confirm.
  id?: string;
  source: 'job_po' | 'stock';
  qty: number;
  status:
    | 'on_order'
    | 'delivered'
    | 'allocated'
    | 'checked_out'
    | 'consumed'
    | 'returned'
    | 'released'
    | 'cancelled';
  order_id?: string | null;
  po_number?: string | null;
  delivery_id?: string | null;
  qty_checked_out?: number;
  qty_consumed?: number;
  // Phase 78-08: the §4 read returns this per stock source so the Job panel can
  // derive the same reserved/checked-out/consumed/returned disposition split the
  // Stock page shows (a partially-progressed row keeps a single status but the
  // counters describe the real split). Materials' formula (allocation-read-service):
  //   reserved = max(qty − qty_checked_out, 0); checked_out = qty_checked_out − qty_returned − qty_consumed.
  qty_returned?: number;
  unit_cost?: number | null;
}

export interface ApiAllocProduct {
  /** Current catalogue pack; quantities/costs are per purchased pack. */
  catalogue_pack_description?: string | null;
  item_code?: string | null;
  catalog_row_id: string;
  product_name: string;
  unit_label: string | null;
  // Slice A (#220): product-level catalog/supplier context (both keyed by
  // catalog_row_id). Null when catalog_row_id is null on the §4 read.
  section: string | null;
  supplier_name: string | null;
  total_qty: number;
  sources: ApiAllocSource[];
}

export interface ApiAllocPhase {
  phase_id: string;
  phase_number: number;
  products: ApiAllocProduct[];
}

export interface ApiWorkItemAllocations {
  target_kind: 'job' | 'ticket';
  work_item_id: string;
  work_item_ion: string | null;
  // jobs return phases[]; tickets return products[] directly.
  phases?: ApiAllocPhase[];
  products?: ApiAllocProduct[];
}

// Phase 78-02 (§4): a warehouse stock row, source for the reserve-from-stock
// picker. The Slice-C GET /api/v1/materials/stock returns these with the
// allocation columns extended; only the picker-relevant fields are required.
export interface ApiStockRow {
  /** Current catalogue pack; quantities/costs are per purchased pack. */
  catalogue_pack_description?: string | null;
  item_code?: string | null;
  catalog_row_id: string;
  product_name: string;
  unit_label: string | null;
  qty_available: number;
  total_qty?: number;
  qty_allocated?: number;
  qty_checked_out_outstanding?: number;
}

export interface ApiMaterialsSupplier {
  id: string;
  name: string;
  status: 'active' | 'inactive';
  contact_name: string | null;
  contact_email: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface ApiMaterialsCatalogRow {
  id: string;
  supplier_id: string;
  item_code: string;
  description: string;
  price: number;
  currency: string;
  unit: string;
  specs: Record<string, unknown>;
  source_extraction_id: string | null;
  last_verified_at: string | null;
  last_verified_by: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ApiExtractedItem {
  id: string;
  ingestion_job_id: string;
  supplier_id: string;
  item_code: string;
  description: string;
  price: number;
  currency: string;
  unit: string;
  specs: Record<string, unknown>;
  status: 'pending' | 'confirmed' | 'corrected' | 'rejected';
  confidence_flags: unknown[];
  source_page: number | null;
  created_at: string;
}

export interface ApiCorrection {
  id: string;
  extracted_item_id: string;
  field_name: string;
  old_value: string | null;
  new_value: string | null;
  corrected_by: string;
  corrected_at: string;
  reason: string | null;
}

/**
 * Structured 409 error from POST /extracted_items/:id/confirm. Plan 04-01's
 * confirm endpoint returns one of two codes; apiFetch's generic Error throw
 * loses the body, so the FE wraps the confirm call in raw fetch to retain
 * code-level access (mirrors the equipment 409 pattern in this file).
 */
export class MaterialsConfirmConflictError extends Error {
  constructor(
    public code: 'ALREADY_RESOLVED' | 'CATALOG_DUPLICATE_ACTIVE',
    message: string,
  ) {
    super(message);
    this.name = 'MaterialsConfirmConflictError';
  }
}

// POST /jobs/:id/materials body — must reference a catalogue entry
export type AddMaterialRequest = {
  product_catalogue_id: string;
  quantity?: number;
  notes?: string;
};

// Reports
export interface ApiUtilisationRow {
  id: string;
  name: string;
  initials: string;
  is_subcontractor: boolean;
  total_hours: string; // scheduled hours (pg numeric as string)
  worked_hours: string; // clocked hours, net of lunch (subs always '0')
  overtime_hours: string; // GH #177: '0' here — overtime comes from the band report
}

// GH #177 item 2: workforce-mix KPI.
export interface ApiActiveStaffCounts {
  total: number;
  operatives: number;
  subcontractors: number;
}

// GH #177 item 7: scheduled-hours operative/subcontractor split.
export interface ApiStaffMix {
  operative_hours: string;
  subcontractor_hours: string;
}

// GH #177 item 8: phase-lifecycle distribution row.
export interface ApiPhaseLifecycleRow {
  bucket: string; // not_started | in_progress | postponed | cancelled | completed
  phase_status: string | null; // provisional|confirmed for not_started; null otherwise
  scheduled_hours: string;
}

// GH #177 items 2 & 3: overtime by operative from the worked-hours band calc.
export interface ApiOvertimeRow {
  id: string;
  name: string;
  initials: string;
  worked_hours: number;
  overtime_hours: number;
  by_multiplier: Record<string, number>;
}

export interface ApiLeaveReportRow {
  id: string;
  name: string;
  initials: string;
  is_subcontractor: boolean;
  leave_type: string;
  days_taken: string;
  entitled_days: string | null;
  xero_taken_days: string | null;
}

export interface ApiStaffLeaveSummary {
  staff_id: string;
  current_leave_type: string | null;
  current_leave_from: string | null;
  current_leave_to: string | null;
  holiday_entitled: number | null;
  holiday_taken: number;
  sick_entitled: number | null;
  /** Null means restricted, not zero sickness. */
  sick_taken: number | null;
  leave_periods: Array<{ from: string; to: string; leave_type: string; days: number }>;
}

export interface ApiConsecutiveDaysRow {
  id: string;
  name: string;
  initials: string;
  is_subcontractor: boolean;
  consecutive_days: number;
  last_worked_date: string | null;
}

export interface ApiSubcontractorRow {
  staff_id: string;
  name: string;
  initials: string;
  job_id: string;
  ion: string;
  job_name: string;
  client_name: string;
  total_hours: string;
  segments: string;
}

export interface ApiEquipmentDemandRow {
  equipment_type_id: string;
  type_name: string;
  // Phase 36-01 (Issue #37): semantic shift — capacity is now SUM of
  // equipment_items.quantity (NULL items contribute 0; counted separately
  // in n_a_items_count). The Reports page UI refresh (full hide-N/A
  // checkbox + visual N/A flag) lands in 36-02.
  total_quantity: string;
  n_a_items_count: string;
  peak_demand: string;
  days_at_capacity: string;
}

// GH #184 widget 1: skill demand row. Returned by GET /reports/skills.
export interface ApiSkillsDemandRow {
  skill_id: string;
  skill_name: string;
  phase_count: string;
  job_count: string;
  pct_share: string; // % of total skill demand (sums to ~100)
}

// GH #184 widget 2: per-van demand row. Returned by GET /reports/vehicles.
export interface ApiVehicleDemandRow {
  vehicle_id: string;
  registration: string;
  make: string;
  model: string;
  booked_days: string;
  unavailable_days: string;
  free_days: string;
  days_in_range: string;
  utilisation: string; // 0–1, booked ÷ days-in-range
}

// Phase 36-02 (Issue #37) mid-loop bundle: per-type drill-down row.
// Returned by GET /reports/equipment/types/:typeId/bookings — denormalised
// (item × date × job) list fed to the Reports page expandable row.
export interface ApiEquipmentTypeBookingRow {
  equipment_item_id: string;
  identifier: string;
  date: string;
  quantity: string;
  job_id: string;
  job_ion: string | null;
  job_name: string | null;
}

// Notifications
export interface AppNotification {
  id: string;
  type: string;
  severity: string;
  staff_id: string | null;
  job_id: string | null;
  title: string;
  message: string;
  metadata: Record<string, unknown>;
  is_read: boolean;
  created_at: string;
}

// Schedule warnings returned alongside assignment creation/update responses
export interface ScheduleWarning {
  code:
    | 'DAILY_OVERTIME'
    | 'WEEKLY_OVERTIME'
    | 'CONSECUTIVE_DAYS'
    | 'SKILL_MISMATCH'
    | 'WTR_CONSECUTIVE_DAYS';
  message: string;
  details: Record<string, unknown>;
}

export interface AssignmentResponse {
  data: ApiAssignment;
  warnings: ScheduleWarning[];
}

// Per-worker schedule warnings for a phase — returned by
// GET /api/v1/jobs/:id/phase-warnings?phaseId=<uuid>. Only workers with ≥1
// warning are included, so the banner renders one row per returned entry.
export interface ApiWorkerWarnings {
  staffId: string;
  warnings: ScheduleWarning[];
}

// ─── API calls ────────────────────────────────────────────────────────────────

export interface ApiMe {
  email: string;
  name: string;
  role: string;
  // 14-05: orthogonal Time PWA access capability — drives Layout's redirect
  // logic for basic_access users + side menu Clock-in entry visibility.
  can_clock_in: boolean;
  staffId: string | null;
  initials: string;
  is_subcontractor: boolean;
  // GH #130: the caller's resolved permission-key set ("feature.action"). Drives
  // all UI gating via usePermissions().can(feature, action). Superadmin = full.
  permissions: string[];
}

// GH #130: the Access Control editor's data shape (GET /admin/rbac-roles).
export interface ApiRbacMatrix {
  features: Record<string, string[]>; // { feature: actions[] }
  notBuilt: string[]; // features defined but not enforced (editable, no wiring)
  editableRoles: string[]; // ['basic_access','viewer','planner','admin']
  roles: Record<string, string[]>; // editable role → granted "feature.action" keys
  superadmin: string[]; // locked-full set (display only, not editable)
  // R-30 (61-01/61-04): keys the matrix editor must render LOCKED — the backend
  // rejects granting them (superadmin-only by construction). Optional for
  // backward compat with cached/older payloads.
  restricted?: string[];
}

// ─── Time entries (admin) ─────────────────────────────────────────────────────

export type ApiTimeEntryStatus = 'active' | 'completed' | 'edited' | 'cancelled';
export type ApiTimeEntrySource = 'field_app' | 'admin_edit' | 'import';
export type ApiGeofenceStatus = 'inside' | 'outside' | 'unknown';
// GH #185: per-entry approval workflow (DISTINCT from edit_requests).
export type ApiTimeEntryApprovalStatus = 'draft' | 'pending_approval' | 'approved' | 'rejected';

export interface ApiTimeEntry {
  id: string;
  staff_id: string;
  job_id: string;
  assignment_segment_id: string | null;
  clock_in_ts: string;
  clock_in_lat: string | null;
  clock_in_lng: string | null;
  clock_in_geofence_status: ApiGeofenceStatus | null;
  clock_out_ts: string | null;
  clock_out_lat: string | null;
  clock_out_lng: string | null;
  clock_out_geofence_status: ApiGeofenceStatus | null;
  status: ApiTimeEntryStatus;
  source: ApiTimeEntrySource;
  notes: string | null;
  // Net deduction applied when computing hours worked. Null = not recorded.
  lunch_break_minutes: number | null;
  created_at: string;
  updated_at: string;
  // Join fields populated by GET /time/entries (LEFT JOIN — null if missing).
  // PATCH response omits these; the list refetches after mutation.
  staff_name?: string | null;
  job_ion?: string | null;
  job_name?: string | null;
  latest_edit_request_status?: 'pending' | 'approved' | 'rejected' | 'withdrawn' | null;
  // Phase 35-01 (Issue #38): id of the most recent edit request for this entry.
  // Drives the inline Review icon's drawer-open path (no separate lookup).
  latest_edit_request_id?: string | null;
  // GH #185: per-entry approval workflow. Present on GET /time/entries rows and
  // on the approval-mutation responses (submit/approve/reject).
  approval_status?: ApiTimeEntryApprovalStatus;
  submitted_by?: string | null;
  submitted_at?: string | null;
  approved_by?: string | null;
  approved_at?: string | null;
  rejected_by?: string | null;
  rejected_at?: string | null;
  rejection_reason?: string | null;
}

export interface ListTimeEntriesFilters {
  staff_id?: string;
  // Issue #174: multi-select operative filter. Serialised as repeated
  // `staff_ids` query params. Empty/omitted = All operatives. The single
  // staff_id above is retained for back-compat (other callers / exports).
  staff_ids?: string[];
  job_id?: string;
  status?: ApiTimeEntryStatus;
  // GH #185: per-entry approval-status filter (distinct from lifecycle status).
  approval_status?: 'draft' | 'pending_approval' | 'approved' | 'rejected';
  clock_in_geofence_status?: ApiGeofenceStatus;
  date_from?: string; // YYYY-MM-DD
  date_to?: string; // YYYY-MM-DD
}

export interface PatchTimeEntryBody {
  clock_in_ts?: string;
  clock_out_ts?: string | null;
  job_id?: string;
  notes?: string | null;
  // #250 item 3: admin add/remove of a lunch break. Explicit 0 = remove;
  // positive value = apply (the `lunch_break` config setting's minutes).
  lunch_break_minutes?: number | null;
}

/**
 * Phase 35-03 (Issue #38 amended scope): GET /time/entries returns rows
 * grouped by assignment segment — one row per assignment
 * segment, with embedded time_entries[] (0..N). No-shows surface as rows
 * with time_entries=[] + is_no_show=true; clock-ins without an assignment
 * are excluded entirely (admin must assign retroactively first, Q6=b).
 */
export interface ApiTimesheetAbsence {
  id: string;
  reason: AbsenceReason;
  pay_treatment: 'xero_leave' | 'unpaid';
  source: 'finance_review' | 'operative_declared';
  notes: string | null;
  resolved_by_name: string | null;
  resolved_at: string;
  updated_at: string;
  xero_leave_status: 'matched' | 'missing' | 'not_required';
}

export interface ApiTimesheetRow {
  // Optional during compatible rollout from the earlier backend response.
  segment_id?: string;
  phase_id?: string | null;
  phase_number?: number | null;
  absence?: ApiTimesheetAbsence | null;
  staff_id: string;
  staff_name: string | null;
  job_id: string;
  job_ion: string | null;
  job_name: string | null;
  scheduled_date: string; // YYYY-MM-DD
  scheduled_start: string; // HH:mm
  scheduled_end: string; // HH:mm
  scheduled_hours: number;
  worked_hours_sum: number;
  time_entries: ApiTimeEntry[]; // 0..N (sorted clock_in_ts ASC by the backend json_agg)
  is_no_show: boolean;
}

export interface ListTimeEntriesResponse {
  data: ApiTimesheetRow[];
  meta: { count: number; limit: number };
}

// ─── Edit requests (admin / planner read) ─────────────────────────────────────

export type ApiEditRequestStatus = 'pending' | 'approved' | 'rejected' | 'withdrawn';

export interface ApiEditRequest {
  id: string;
  time_entry_id: string;
  requested_by_staff_id: string;
  // Backend zod enforces clock_in_ts/clock_out_ts/job_id/notes shape; treat as opaque here.
  requested_changes: Record<string, unknown>;
  reason: string | null;
  status: ApiEditRequestStatus;
  reviewed_by_staff_id: string | null;
  reviewed_at: string | null;
  review_notes: string | null;
  created_at: string;
  updated_at: string;
  // Joined by GET /time/edit-requests (LEFT JOIN — nullable when row missing).
  requester_name: string | null;
  requester_email: string | null;
  requester_work_email: string | null;
  entry_clock_in_ts: string | null;
  entry_clock_out_ts: string | null;
  entry_job_id: string | null;
  entry_notes: string | null;
  job_ion: string | null;
  job_name: string | null;
}

export interface ListEditRequestsFilters {
  status?: ApiEditRequestStatus;
  requested_by_staff_id?: string;
  time_entry_id?: string;
  date_from?: string;
  date_to?: string;
}

export interface ListEditRequestsResponse {
  data: ApiEditRequest[];
  meta: { count: number; limit: number };
}

// Phase 79-02: an auto-capped clock-out awaiting pre-payroll review (79-01
// contract). The worker's attempted clock-out ran past the scheduled end, so
// the backend capped it at scheduled_end and flagged the entry for review.
export interface ApiTimeIssue {
  id: string;
  staff_id: string;
  staff_name: string;
  job_id: string;
  job_label: string;
  clock_in_ts: string;
  attempted_clock_out_ts: string;
  capped_clock_out_ts: string;
  scheduled_end: string;
}

// Absence reason for resolving / re-categorising an open no-show (missed
// scheduled clock-in). `null` reason (on PATCH) clears the categorisation.
export type AbsenceReason = 'holiday' | 'sick' | 'unauthorised_absence' | 'family_emergency';

// An open no-show: a scheduled assignment segment the worker never clocked into.
// staff_name / job_label may be null when the joined record is unavailable.
export interface ApiNoShow {
  segment_id: string;
  staff_id: string;
  staff_name: string | null;
  job_id: string;
  job_label: string | null;
  date: string;
  start_time: string;
  end_time: string;
}

// Reason an operative gives when self-reporting a missed/interrupted shift.
// Overlaps AbsenceReason for the "genuine absence" cases (sick / family
// emergency) but ALSO covers two operational causes that are NOT absences
// (technical_issue / transport_client_issue) — those resolve via a back-filled
// time entry (Path A), not a categorised absence (Path B).
export type DeclarationReason =
  | 'sick'
  | 'family_emergency'
  | 'technical_issue'
  | 'transport_client_issue';

// A pending operative self-declaration: the SAME segment shape as ApiNoShow,
// plus the declared reason/note and when it was declared. Resolving it via the
// no-show resolve/createEntry endpoints (keyed by segment_id) auto-marks the
// declaration actioned server-side.
export interface ApiDeclaration {
  segment_id: string;
  staff_id: string;
  staff_name: string | null;
  job_id: string;
  job_label: string | null;
  date: string;
  start_time: string;
  end_time: string;
  reason: DeclarationReason;
  note: string | null;
  declared_at: string;
}

// A Xero leave type (used to map an absence reason onto Xero payroll leave).
export interface ApiXeroLeaveType {
  leaveTypeID: string;
  name: string;
}

export interface ReviewEditRequestBody {
  decision: 'approved' | 'rejected';
  review_notes?: string;
}

export interface ReviewEditRequestResult {
  editRequest: ApiEditRequest;
  updatedTimeEntry: ApiTimeEntry | null;
}

export interface PayrollExportFilters {
  date_from: string;
  date_to: string;
  staff_id?: string;
  job_id?: string;
}

export interface CreatePayrollExportBody extends PayrollExportFilters {
  idempotency_key: string;
  supersedes_id?: string;
  correction_reason?: string;
}

export interface PayrollExport {
  id: string;
  created_at: string;
  created_by: string;
  filters: PayrollExportFilters;
  row_count: number;
  content_sha256: string;
  schema_version: 1;
  supersedes_id: string | null;
  correction_reason: string | null;
  delivery: {
    status: 'creating' | 'writing' | 'complete' | 'retryable' | 'uncertain';
    sheet_id: string | null;
    url: string | null;
    last_error_code: string | null;
  } | null;
}

export interface PayrollExportList {
  data: PayrollExport[];
  meta: { count: number; limit: number; offset: number };
}

export interface ExportSheetResult {
  batch_id: string;
  sheet_id: string;
  url: string;
  row_count: number;
  date_from: string;
  date_to: string;
}

// ─── Unified audit log (admin) ────────────────────────────────────────────────

export type ApiAuditSource = 'audit_log' | 'lifecycle_events' | 'audit_events';

export interface ApiUnifiedAuditRow {
  id: string;
  occurred_at: string;
  source: ApiAuditSource;
  actor_email: string | null;
  entity_type: string | null;
  entity_id: string | null;
  summary: string;
  details: Record<string, unknown>;
}

export interface ListAuditFilters {
  date_from?: string;
  date_to?: string;
  source?: ApiAuditSource;
  actor_email?: string;
  entity_type?: string;
}

export interface ListAuditMeta {
  count: number;
  limit: number;
  capped: boolean;
}

export interface ListAuditResponse {
  data: ApiUnifiedAuditRow[];
  meta: ListAuditMeta;
}

export const api = {
  auth: {
    login: (idToken: string, returnTo?: string) =>
      apiFetch<
        SingleResponse<{
          email: string;
          name: string;
          role: string;
          can_clock_in: boolean;
          token: string;
          redirect_to?: string;
        }>
      >('/api/v1/auth/login', {
        method: 'POST',
        body: JSON.stringify({ idToken, ...(returnTo ? { return_to: returnTo } : {}) }),
      }),
    logout: async (): Promise<void> => {
      const response = await fetch(`${BASE_URL}/api/v1/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Could not confirm sign-out');
    },
  },

  integrations: {
    hubspotSync: () =>
      apiFetch<SingleResponse<unknown>>('/api/v1/integrations/hubspot/sync', { method: 'POST' }),
    hubspotStatus: () =>
      apiFetch<SingleResponse<{ last_sync_at: string | null; last_status: string | null }>>(
        '/api/v1/integrations/hubspot/status',
      ),
    ticketSync: () =>
      apiFetch<SingleResponse<unknown>>('/api/v1/integrations/hubspot/ticket-sync', {
        method: 'POST',
      }),
    xeroConnect: () =>
      apiFetch<SingleResponse<{ url: string; state: string }>>('/api/v1/integrations/xero/connect'),
    xeroSync: () =>
      apiFetch<SingleResponse<unknown>>('/api/v1/integrations/xero/sync', { method: 'POST' }),
    xeroStatus: () =>
      apiFetch<
        SingleResponse<{
          last_sync_at: string | null;
          last_status: string | null;
          // Phase 63-01: backend-derived live re-auth state (token dead/revoked).
          needsReauth?: boolean;
          connectionSetupRequired?: boolean;
        }>
      >('/api/v1/integrations/xero/status'),
    // Xero payroll leave types, for mapping an absence reason onto Xero leave.
    xeroLeaveTypes: () =>
      apiFetch<SingleResponse<ApiXeroLeaveType[]>>('/api/v1/integrations/xero/leave-types').then(
        (r) => r.data,
      ),
  },

  me: {
    get: () => apiFetch<SingleResponse<ApiMe>>('/api/v1/me').then((r) => r.data),
  },

  // Phase 65-03: NL assistant. Returns the engine's answer (route wraps it in
  // { data: ... }; we unwrap). Non-ok → ApiHttpError(status) for the caller to
  // map (429 rate-limit, 403 forbidden).
  assistant: {
    query: (body: {
      message: string;
      recentTurns: { role: 'user' | 'assistant'; content: string }[];
    }) =>
      apiFetch<
        SingleResponse<{
          answer: string;
          toolsUsed: string[];
          data?: unknown;
          noTool?: boolean;
          exportable?: {
            reportType: 'job_costing' | 'labour_payroll' | 'materials_spend';
            params: { from?: string; to?: string };
          } | null;
        }>
      >('/api/v1/assistant/query', { method: 'POST', body: JSON.stringify(body) }).then(
        (r) => r.data,
      ),
    // Phase 65: export an exportable answer to a Google Sheet. Route wraps the
    // link in { data: { url } }; we unwrap. Non-ok (403/429/503) → ApiHttpError.
    export: (body: {
      reportType: 'job_costing' | 'labour_payroll' | 'materials_spend';
      params: { from?: string; to?: string };
    }) =>
      apiFetch<SingleResponse<{ url: string }>>('/api/v1/assistant/export', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
  },

  // ── Phase 70: native CB Materials pages — reserved API namespaces ──────────────
  // Each is filled by its page plan against /api/v1/materials/* (70-02..05). Kept as
  // separate keys so the parallel page builds edit ISOLATED blocks, not shared lines.
  materialsSuppliers: {
    // 70-02 — suppliers / ingestion_jobs / extracted_items / extraction_prompts / supplier_flags
  },
  materialsCatalogue: {
    // 70-03 — GET catalog + manual add/edit/delete
  },
  materialsOrders: {
    // 70-04 — orders + xero/* (contacts/accounts/submit) + book-to-stock/job + PO status
  },
  materialsStock: {
    // 70-05 — stock items / deliveries / per-job allocations
  },

  skills: {
    list: () => apiFetch<ListResponse<ApiSkill>>('/api/v1/skills').then((r) => r.data),
    create: (body: { name: string; description?: string }) =>
      apiFetch<SingleResponse<ApiSkill>>('/api/v1/skills', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    update: (id: string, body: { name?: string; description?: string | null }) =>
      apiFetch<SingleResponse<ApiSkill>>(`/api/v1/skills/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    delete: (id: string) => apiFetch<void>(`/api/v1/skills/${id}`, { method: 'DELETE' }),
  },

  vehicles: {
    list: (opts?: { includeInactive?: boolean }) => {
      const qs = opts?.includeInactive ? '?include_inactive=true' : '';
      return apiFetch<ListResponse<ApiVehicle>>(`/api/v1/vehicles${qs}`).then((r) => r.data);
    },
    create: (body: { registration: string; make: string; model: string }) =>
      apiFetch<SingleResponse<ApiVehicle>>('/api/v1/vehicles', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    update: (
      id: string,
      body: { registration?: string; make?: string; model?: string; is_active?: boolean },
    ) =>
      apiFetch<SingleResponse<ApiVehicle>>(`/api/v1/vehicles/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    delete: (id: string) => apiFetch<void>(`/api/v1/vehicles/${id}`, { method: 'DELETE' }),

    // Phase 38-01: feeds the JobDetailPanel +Add form chip filter. Returns the
    // union of job_vehicles + vehicle_unavailability dates for this vehicle in
    // the requested range. Empty array if vehicle has no conflicts in range.
    busyDates: (id: string, from: string, to: string) =>
      apiFetch<{ data: { busy_dates: string[] } }>(
        `/api/v1/vehicles/${id}/busy-dates?from=${from}&to=${to}`,
      ).then((r) => r.data.busy_dates),

    unavailability: {
      list: (vehicleId: string) =>
        apiFetch<ListResponse<ApiVehicleUnavailability>>(
          `/api/v1/vehicles/${vehicleId}/unavailability`,
        ).then((r) => r.data),
      create: (
        vehicleId: string,
        body: { date: string; reason: VehicleUnavailabilityReason; notes?: string },
      ) =>
        apiFetch<SingleResponse<ApiVehicleUnavailability>>(
          `/api/v1/vehicles/${vehicleId}/unavailability`,
          { method: 'POST', body: JSON.stringify(body) },
        ).then((r) => r.data),
      update: (
        vehicleId: string,
        entryId: string,
        body: { reason?: VehicleUnavailabilityReason; notes?: string | null },
      ) =>
        apiFetch<SingleResponse<ApiVehicleUnavailability>>(
          `/api/v1/vehicles/${vehicleId}/unavailability/${entryId}`,
          { method: 'PATCH', body: JSON.stringify(body) },
        ).then((r) => r.data),
      delete: (vehicleId: string, entryId: string) =>
        apiFetch<void>(`/api/v1/vehicles/${vehicleId}/unavailability/${entryId}`, {
          method: 'DELETE',
        }),
    },
  },

  // Phase 38-02: planner-facing equipment endpoints (read-only). Admin write
  // paths for equipment items + types live under api.admin.equipment* (see
  // below). This namespace is the home for any future planner-facing
  // equipment reads; starts with the one endpoint feeding the JobDetailPanel
  // +Add Equipment chip filter.
  equipment: {
    busyDates: (itemId: string, from: string, to: string) =>
      apiFetch<{ data: { busy_dates: string[] } }>(
        `/api/v1/equipment/items/${itemId}/busy-dates?from=${from}&to=${to}`,
      ).then((r) => r.data.busy_dates),
  },

  staff: {
    list: (opts?: { includeInactive?: boolean }) => {
      const qs = opts?.includeInactive ? '?include_inactive=true' : '';
      return apiFetch<ListResponse<ApiStaff>>(`/api/v1/staff${qs}`).then((r) => r.data);
    },
    get: (id: string) =>
      apiFetch<SingleResponse<ApiStaff>>(`/api/v1/staff/${id}`).then((r) => r.data),
    getSkills: (id: string) =>
      apiFetch<ListResponse<ApiStaffSkill>>(`/api/v1/staff/${id}/skills`).then((r) => r.data),
    addSkill: (id: string, body: { skill_id: string; level?: string }) =>
      apiFetch<SingleResponse<ApiStaffSkill>>(`/api/v1/staff/${id}/skills`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    updateSkill: (staffId: string, skillId: string, body: { level: string | null }) =>
      apiFetch<SingleResponse<ApiStaffSkill>>(`/api/v1/staff/${staffId}/skills/${skillId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    removeSkill: (staffId: string, skillId: string) =>
      apiFetch<void>(`/api/v1/staff/${staffId}/skills/${skillId}`, { method: 'DELETE' }),
    patch: (
      id: string,
      body: {
        name?: string;
        email?: string;
        defaultHours?: number;
        isActive?: boolean;
        staffRole?: 'operative' | 'manager' | 'back_office';
        initials?: string;
        knownAs?: string | null;
        workEmail?: string | null;
        // #234 Task 7: labour cost £/hr (finance/admin only — gated server-side
        // by staff.insert_update). number = set, null = clear.
        costRate?: number | null;
      },
    ) =>
      apiFetch<SingleResponse<ApiStaff>>(`/api/v1/staff/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    leaveSummary: (year?: number) => {
      const q = year ? `?year=${year}` : '';
      return apiFetch<{ data: ApiStaffLeaveSummary[]; year: number }>(
        `/api/v1/staff/leave-summary${q}`,
      ).then((r) => r.data);
    },
    consecutiveDays: (date?: string) => {
      const q = date ? `?date=${encodeURIComponent(date)}` : '';
      return apiFetch<{ data: Record<string, number> }>(`/api/v1/staff/consecutive-days${q}`).then(
        (r) => r.data,
      );
    },
    consecutiveDaysRange: (from: string, to: string) =>
      apiFetch<{ data: Record<string, Record<string, number>> }>(
        `/api/v1/staff/consecutive-days/range?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      ).then((r) => r.data),
  },

  workItems: {
    list: (params?: { status?: string; source?: string }) => {
      const q = new URLSearchParams();
      if (params?.status) q.set('status', params.status);
      if (params?.source) q.set('source', params.source);
      const qs = q.toString();
      return apiFetch<ListResponse<ApiWorkItem>>(
        qs ? `/api/v1/work-items?${qs}` : '/api/v1/work-items',
      ).then((r) => r.data);
    },
    // Phase 41-02 (#65): location verification pre-flight for the Scheduling Wizard.
    verifyLocation: (id: string, address: string) =>
      apiFetch<SingleResponse<{ matches: ApiGeocodeMatch[] }>>(
        `/api/v1/work-items/${id}/verify-location`,
        {
          method: 'POST',
          body: JSON.stringify({ address }),
        },
      ).then((r) => r.data),
    confirmLocation: (id: string, picked: ApiGeocodeMatch) =>
      apiFetch<SingleResponse<{ ok: true; hubspot_synced: boolean; formatted_address: string }>>(
        `/api/v1/work-items/${id}/confirm-location`,
        {
          method: 'POST',
          body: JSON.stringify(picked),
        },
      ).then((r) => r.data),
    schedule: (
      id: string,
      body: {
        startDate?: string;
        endDate?: string;
        notes?: string;
        contractsManagerId?: string;
        status?: 'provisional' | 'confirmed';
        // Phase 39-03: explicit multi-phase wizard payload. When provided
        // (length >= 1) the backend creates the job + N phases atomically with
        // explicit phase_number per entry (Q9 stable numbering). When absent,
        // legacy flat-fields path: backend creates a single phase_number=1.
        phases?: ApiWorkItemSchedulePhaseInput[];
      },
    ) =>
      apiFetch<SingleResponse<ApiJob>>(`/api/v1/work-items/${id}/schedule`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
  },

  jobs: {
    list: (params?: { month?: number; year?: number; status?: string }) => {
      const q = new URLSearchParams();
      if (params?.month) q.set('month', String(params.month));
      if (params?.year) q.set('year', String(params.year));
      if (params?.status) q.set('status', params.status);
      const qs = q.toString();
      return apiFetch<ListResponse<ApiJob>>(qs ? `/api/v1/jobs?${qs}` : '/api/v1/jobs').then(
        (r) => r.data,
      );
    },
    get: (id: string) => apiFetch<SingleResponse<ApiJob>>(`/api/v1/jobs/${id}`).then((r) => r.data),
    update: (
      id: string,
      // name / clientName / siteAddress are HubSpot-owned — use syncFromHubSpot to refresh them.
      body: {
        startDate?: string;
        endDate?: string | null;
        notes?: string | null;
        dailyScopes?: Record<string, string>;
        contractsManagerId?: string | null;
        // 80-02: the phase confirm/un-confirm field (was the booking-status field). The
        // BE updateJob drives a job_phases.status transition; lock guard →
        // 422 PHASE_STATUS_LOCKED; composed PATCH response exposes phase_status.
        status?: 'provisional' | 'confirmed';
        // Phase 47-01 (GH #67): per-phase operative-visibility flag. Pair with
        // phaseId to target the addressed phase; gates whether that phase's
        // FUTURE allocations surface on the /time PWA schedule.
        viewableByOperatives?: boolean;
        // Phase 26-07: assignment cascade strategy on date change.
        // 'preserve' (default) prunes out-of-range segments only.
        // 'shift' moves active segments by (new_start - old_start) days.
        // 'remove' deletes ALL active segments.
        // Phase 47 (GH #104) added whole-phase reassignment:
        // 'transfer' moves dates + all NON-conflicting staff/vehicles/equipment
        //   (conflicting ones are released);
        // 'clear' moves dates + releases ALL staff/vehicles/equipment.
        dateChangeStrategy?: 'preserve' | 'shift' | 'remove' | 'transfer' | 'clear';
        // Phase 40-01 fixup: scope the PATCH to a specific phase. Without
        // this the BE hardcodes Phase 1 (legacy single-phase wire shape),
        // which silently broke the date pencil on any non-Phase-1 phase
        // post-Migration-067 (segment shift would trip the range trigger).
        phaseId?: string;
      },
    ) =>
      apiFetch<
        SingleResponse<ApiJob> & {
          pruned: { count: number; staffNames: string[] };
          // Only present when dateChangeStrategy is 'shift' or 'remove'.
          warnings?: ScheduleWarning[];
          // Phase 47 (GH #104): present when dateChangeStrategy is 'transfer'
          // or 'clear'. `moved` here reflects what ACTUALLY transferred.
          reassignment?: ReassignmentAnalysis;
        }
      >(`/api/v1/jobs/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    // Phase 47 (GH #104): dry-run conflict analysis for a whole-phase date
    // move. Returns the analysis only (no commit); `moved` reflects what WOULD
    // transfer cleanly. The reassignment dialog calls this for Step-1 preview.
    reschedulePreview: (
      jobId: string,
      body: { phaseId: string; startDate: string; endDate: string },
    ) =>
      apiFetch<SingleResponse<ReassignmentAnalysis>>(`/api/v1/jobs/${jobId}/reschedule-preview`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    syncFromHubSpot: (id: string) =>
      apiFetch<SingleResponse<ApiJob>>(`/api/v1/jobs/${id}/sync-from-hubspot`, {
        method: 'POST',
      }).then((r) => r.data),
    transition: (id: string, status: ApiJob['status'], reason?: string) =>
      apiFetch<SingleResponse<ApiJob>>(`/api/v1/jobs/${id}/transition`, {
        method: 'POST',
        body: JSON.stringify({ status, ...(reason ? { reason } : {}) }),
      }).then((r) => r.data),
    // GH #108: optional `reason` is persisted as postponed_reason on the BE
    // snapshot (Migration 065). Backward-compatible — omitting it posts no body.
    postpone: (id: string, reason?: string): Promise<{ id: string; status: string }> =>
      apiFetch<SingleResponse<{ id: string; status: string }>>(`/api/v1/jobs/${id}/postpone`, {
        method: 'POST',
        ...(reason && reason.trim() ? { body: JSON.stringify({ reason: reason.trim() }) } : {}),
      }).then((r) => r.data),
    documents: (id: string) =>
      apiFetch<{ data: { folder_linked: boolean; files: ApiDocument[] } }>(
        `/api/v1/jobs/${id}/documents`,
      ).then((r) => r.data.files),
    // Per-phase profitability: one cost/profit row per phase for this job.
    // Returns the unwrapped { data: PhaseCost[] } array.
    phaseCosts: (id: string) =>
      apiFetch<ListResponse<PhaseCost>>(`/api/v1/jobs/${id}/phase-costs`).then((r) => r.data),
    // Per-worker schedule warnings for the given phase. Returns the unwrapped
    // { data: ApiWorkerWarnings[] } array — one entry per assigned worker who
    // has ≥1 warning. Drives the per-worker "Assignment warnings" banner.
    phaseWarnings: (jobId: string, phaseId: string) =>
      apiFetch<ListResponse<ApiWorkerWarnings>>(
        `/api/v1/jobs/${jobId}/phase-warnings?phaseId=${phaseId}`,
      ).then((r) => r.data),
    materials: (id: string, phaseId?: string) =>
      apiFetch<ListResponse<ApiMaterial>>(
        `/api/v1/jobs/${id}/materials${phaseId ? `?phase_id=${phaseId}` : ''}`,
      ).then((r) => r.data),
    addMaterial: (id: string, body: AddMaterialRequest & { phase_id?: string }) =>
      apiFetch<SingleResponse<ApiMaterial>>(`/api/v1/jobs/${id}/materials`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    deleteMaterial: (jobId: string, materialId: string) =>
      apiFetch<void>(`/api/v1/jobs/${jobId}/materials/${materialId}`, { method: 'DELETE' }),
    updateMaterial: (
      jobId: string,
      materialId: string,
      body: { quantity?: number | null; notes?: string | null },
    ) =>
      apiFetch<SingleResponse<ApiMaterial>>(`/api/v1/jobs/${jobId}/materials/${materialId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    equipment: (id: string, phaseId?: string) =>
      apiFetch<ListResponse<ApiJobEquipment>>(
        `/api/v1/jobs/${id}/equipment${phaseId ? `?phase_id=${phaseId}` : ''}`,
      ).then((r) => r.data),
    // Phase 36-02 (Issue #37): bulk-add bypasses apiFetch so we can intercept
    // the 409 body shape and throw the typed AddEquipmentConflictError carrying
    // the conflicting_dates array. UI can `instanceof` check + list every
    // offending date inline. Mirrors api.jobs.addVehicles pattern from 34-03.
    addEquipment: async (
      id: string,
      body: {
        equipment_item_id: string;
        dates: string[];
        quantity?: number;
        phase_id?: string;
      },
    ): Promise<ApiJobEquipment[]> => {
      // ARCH-4 (plan 50-09): goes through apiFetch (shared dev-user header +
      // 401 redirect + non-ok handling); the 409 body is recovered from the
      // thrown ApiHttpError and remapped to the typed AddEquipmentConflictError.
      try {
        const json = await apiFetch<ListResponse<ApiJobEquipment>>(`/api/v1/jobs/${id}/equipment`, {
          method: 'POST',
          body: JSON.stringify(body),
        });
        return json.data;
      } catch (err) {
        if (err instanceof ApiHttpError && err.status === 409) {
          const parsed = (err.body ?? {}) as { details?: AddEquipmentConflictDetails };
          throw new AddEquipmentConflictError(parsed.details?.conflicting_dates ?? []);
        }
        throw err;
      }
    },
    // Phase 36-01: per-day quantity edit.
    patchEquipment: (
      jobId: string,
      equipmentItemId: string,
      date: string,
      body: { quantity: number },
    ) =>
      apiFetch<SingleResponse<ApiJobEquipment>>(
        `/api/v1/jobs/${jobId}/equipment/${equipmentItemId}/${date}`,
        { method: 'PATCH', body: JSON.stringify(body) },
      ).then((r) => r.data),
    deleteEquipment: (jobId: string, equipmentItemId: string, date: string) =>
      apiFetch<void>(`/api/v1/jobs/${jobId}/equipment/${equipmentItemId}/${date}`, {
        method: 'DELETE',
      }),
    // Phase 34-03 (Issue #36): per-job vehicle assignment.
    // Phase 39-07: optional `phaseId` filter mirrors equipment/skills/materials.
    vehicles: (id: string, phaseId?: string) =>
      apiFetch<ListResponse<ApiJobVehicle>>(
        `/api/v1/jobs/${id}/vehicles${phaseId ? `?phase_id=${phaseId}` : ''}`,
      ).then((r) => r.data),
    // Bulk-add. ARCH-4 (plan 50-09): goes through apiFetch (shared dev-user
    // header + 401 redirect + non-ok handling); on 409 the body is recovered
    // from the thrown ApiHttpError and remapped to AddVehiclesConflictError
    // carrying the structured `conflicts` array so the UI can list them inline.
    addVehicles: async (
      id: string,
      body: { vehicle_id: string; dates: string[]; phase_id?: string },
    ): Promise<ApiJobVehicle[]> => {
      try {
        const json = await apiFetch<ListResponse<ApiJobVehicle>>(`/api/v1/jobs/${id}/vehicles`, {
          method: 'POST',
          body: JSON.stringify(body),
        });
        return json.data;
      } catch (err) {
        if (err instanceof ApiHttpError && err.status === 409) {
          const parsed = (err.body ?? {}) as { details?: AddVehiclesConflictDetails };
          // Maintenance has no conflicting job. Keep the server message so
          // the form's normal error path shows the unavailable dates.
          if (parsed.details?.unavailable_dates?.length) throw err;
          throw new AddVehiclesConflictError(parsed.details?.conflicts ?? []);
        }
        throw err;
      }
    },
    deleteVehicle: (jobId: string, vehicleId: string, date: string) =>
      apiFetch<void>(`/api/v1/jobs/${jobId}/vehicles/${vehicleId}/${date}`, { method: 'DELETE' }),
    skills: (id: string, phaseId?: string) =>
      apiFetch<ListResponse<ApiSkill>>(
        `/api/v1/jobs/${id}/skills${phaseId ? `?phase_id=${phaseId}` : ''}`,
      ).then((r) => r.data),
    addSkill: (jobId: string, skillId: string, phaseId?: string) =>
      apiFetch<SingleResponse<ApiSkill>>(`/api/v1/jobs/${jobId}/skills`, {
        method: 'POST',
        body: JSON.stringify({ skill_id: skillId, ...(phaseId ? { phase_id: phaseId } : {}) }),
      }).then((r) => r.data),
    // Phase 40-01 fixup (Tom UAT 2026-05-26): per-phase scope. Without
    // phaseId, the BE deletes ALL phase rows for (job, skill) — a planner
    // removing "Coatings" from Phase 3 silently dropped it from Phase 1 too.
    removeSkill: (jobId: string, skillId: string, phaseId?: string) =>
      apiFetch<void>(
        `/api/v1/jobs/${jobId}/skills/${skillId}${phaseId ? `?phase_id=${phaseId}` : ''}`,
        { method: 'DELETE' },
      ),
  },

  board: {
    month: (year: number, month: number) =>
      apiFetch<SingleResponse<ApiBoardMonth>>(`/api/v1/board?year=${year}&month=${month}`).then(
        (r) => r.data,
      ),
    week: (date: string) =>
      apiFetch<SingleResponse<ApiBoardWeek>>(`/api/v1/board/week?date=${date}`).then((r) => r.data),
    day: (date: string) =>
      apiFetch<SingleResponse<ApiBoardDay>>(`/api/v1/board/day?date=${date}`).then((r) => r.data),
  },

  // Phase 39-07: Phase CRUD client. Wraps the 39-02 backend endpoints
  // (GET added in 39-07 as a Spec [2] amendment — the original 39-02 only
  // built POST + the per-phase PATCH/DELETE/cancel). Used by the
  // JobDetailPanel phase navigator + Add Phase + Cancel Phase actions.
  phases: {
    list: (jobId: string) =>
      apiFetch<ListResponse<ApiPhase>>(`/api/v1/jobs/${jobId}/phases`).then((r) => r.data),
    create: (jobId: string, body: PhaseCreateBody) =>
      apiFetch<SingleResponse<ApiPhase>>(`/api/v1/jobs/${jobId}/phases`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    update: (phaseId: string, body: PhaseUpdateBody) =>
      apiFetch<SingleResponse<ApiPhase>>(`/api/v1/phases/${phaseId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    cancel: (phaseId: string, body: PhaseCancelBody) =>
      apiFetch<SingleResponse<ApiPhase>>(`/api/v1/phases/${phaseId}/cancel`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    delete: (phaseId: string) => apiFetch<void>(`/api/v1/phases/${phaseId}`, { method: 'DELETE' }),
    // Phase 41-03 (#66): per-phase Complete / Uncomplete with cascade-up + HubSpot push.
    complete: (phaseId: string) =>
      apiFetch<
        SingleResponse<{
          ok: true;
          phase_status: 'completed';
          job_status: 'scheduled' | 'completed';
          hubspot_synced: boolean;
        }>
      >(`/api/v1/phases/${phaseId}/complete`, { method: 'POST' }).then((r) => r.data),
    uncomplete: (phaseId: string) =>
      apiFetch<
        SingleResponse<{
          ok: true;
          phase_status: 'confirmed';
          job_status: 'scheduled' | 'completed';
          hubspot_synced: boolean;
        }>
      >(`/api/v1/phases/${phaseId}/uncomplete`, { method: 'POST' }).then((r) => r.data),
  },

  leave: {
    list: (params?: { staffId?: string; from?: string; to?: string }) => {
      const q = new URLSearchParams();
      if (params?.staffId) q.set('staffId', params.staffId);
      if (params?.from) q.set('from', params.from);
      if (params?.to) q.set('to', params.to);
      const qs = q.toString();
      return apiFetch<ListResponse<ApiLeaveEntry>>(
        qs ? `/api/v1/leave?${qs}` : '/api/v1/leave',
      ).then((r) => r.data);
    },
  },

  assignments: {
    list: (params?: { jobId?: string; staffId?: string; date?: string }) => {
      const q = new URLSearchParams();
      if (params?.jobId) q.set('jobId', params.jobId);
      if (params?.staffId) q.set('staffId', params.staffId);
      if (params?.date) q.set('date', params.date);
      const qs = q.toString();
      return apiFetch<ListResponse<ApiAssignment>>(
        qs ? `/api/v1/assignments?${qs}` : '/api/v1/assignments',
      ).then((r) => r.data);
    },
    create: (body: {
      jobId: string;
      staffId: string;
      date: string;
      startTime?: string;
      endTime?: string;
      role?: string;
      // Phase 39-07: optional — BE defaults to earliest non-cancelled phase
      // when absent (legacy backwards-compat + same NOT-NULL latent bug fix).
      phaseId?: string;
    }) =>
      apiFetch<AssignmentResponse>('/api/v1/assignments', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    createBatch: (body: {
      jobId: string;
      staffId: string;
      segments: { date: string; startTime: string; endTime: string }[];
      phaseId?: string;
    }) =>
      apiFetch<{ data: ApiAssignment[]; warnings: ScheduleWarning[] }>(
        '/api/v1/assignments/batch',
        { method: 'POST', body: JSON.stringify(body) },
      ),
    delete: (id: string) => apiFetch<void>(`/api/v1/assignments/${id}`, { method: 'DELETE' }),
    update: (id: string, body: { startTime?: string; endTime?: string }) =>
      apiFetch<AssignmentResponse>(`/api/v1/assignments/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    setTeamLeader: (id: string, isTeamLeader: boolean) =>
      apiFetch<{
        data: ApiAssignment;
        previousTeamLeader: { assignmentId: string; staffId: string } | null;
      }>(`/api/v1/assignments/${id}/team-leader`, {
        method: 'PATCH',
        body: JSON.stringify({ isTeamLeader }),
      }),
    checkConflicts: (params: {
      staffId: string;
      dates: string[];
      startTime: string;
      endTime: string;
    }): Promise<
      Array<{
        date: string;
        hasConflict: boolean;
        conflicts: Array<{
          id: string;
          job_id: string;
          start_time: string;
          end_time: string;
          ion: string;
          job_name?: string;
          // Phase 40-01 fixup: BE projects phase context so the FE conflict
          // label can disambiguate which phase of a job the staff is busy on.
          phase_id?: string;
          phase_number?: number;
        }>;
      }>
    > =>
      Promise.all(
        params.dates.map((date) =>
          apiFetch<{
            hasConflict: boolean;
            conflicts: Array<{
              id: string;
              job_id: string;
              start_time: string;
              end_time: string;
              ion: string;
              job_name?: string;
              phase_id?: string;
              phase_number?: number;
            }>;
          }>(
            `/api/v1/assignments/conflicts?staffId=${encodeURIComponent(params.staffId)}&date=${date}&startTime=${encodeURIComponent(params.startTime)}&endTime=${encodeURIComponent(params.endTime)}`,
          ).then((r) => ({ date, ...r })),
        ),
      ),
    // Issue #255 (Facet 1): per-date free-interval availability. Unlike
    // checkConflicts (which asks "does THIS proposed window overlap?"), this
    // asks "does a free slot exist at all within the Working Day?" and returns
    // the free intervals + a suggested (largest-gap) slot the dialog uses to
    // seed a non-overlapping default. One request per date (mirrors
    // checkConflicts); order preserved by Promise.all.
    dayAvailability: (params: {
      staffId: string;
      dates: string[];
      durationMinutes?: number;
    }): Promise<ApiDayAvailability[]> =>
      Promise.all(
        params.dates.map((date) =>
          apiFetch<Omit<ApiDayAvailability, 'date'>>(
            `/api/v1/assignments/day-availability?staffId=${encodeURIComponent(params.staffId)}&date=${date}` +
              (params.durationMinutes ? `&durationMinutes=${params.durationMinutes}` : ''),
          ).then((r) => ({ ...r, date })),
        ),
      ),
    wtrPreCheck: (params: {
      staffId: string;
      fromDate: string;
      toDate: string;
      startTime: string;
      endTime: string;
    }): Promise<Record<string, WtrBlockDetail[]>> =>
      apiFetch<{ data: { blockedDates: Record<string, WtrBlockDetail[]> } }>(
        `/api/v1/assignments/wtr-pre-check?staffId=${encodeURIComponent(params.staffId)}&fromDate=${params.fromDate}&toDate=${params.toDate}&startTime=${encodeURIComponent(params.startTime)}&endTime=${encodeURIComponent(params.endTime)}`,
      ).then((r) => r.data.blockedDates),
  },

  snapshots: {
    list: (params?: { from?: string; to?: string; trigger?: string }) => {
      const q = new URLSearchParams();
      if (params?.from) q.set('from', params.from);
      if (params?.to) q.set('to', params.to);
      if (params?.trigger) q.set('trigger', params.trigger);
      const qs = q.toString();
      return apiFetch<ListResponse<ApiSnapshot>>(
        qs ? `/api/v1/snapshots?${qs}` : '/api/v1/snapshots',
      ).then((r) => r.data);
    },
    get: (id: string) =>
      apiFetch<SingleResponse<ApiSnapshotDetail>>(`/api/v1/snapshots/${id}`).then((r) => r.data),
    create: (date?: string) =>
      apiFetch<SingleResponse<{ id: string; snapshot_date: string; trigger: string }>>(
        '/api/v1/snapshots',
        { method: 'POST', body: JSON.stringify(date ? { date } : {}) },
      ).then((r) => r.data),
    restorePreview: (id: string) =>
      apiFetch<SingleResponse<ApiPlanningRestorePreview>>(
        `/api/v1/snapshots/${id}/restore-preview`,
      ).then((r) => r.data),
    restoreRequests: () =>
      apiFetch<ListResponse<ApiPlanningRestoreRequestSummary>>(
        '/api/v1/snapshots/restore-requests',
      ).then((r) => r.data),
    restoreRequest: (id: string) =>
      apiFetch<SingleResponse<ApiPlanningRestoreRequest>>(
        `/api/v1/snapshots/restore-requests/${id}`,
      ).then((r) => r.data),
    requestRestore: (body: ApiPlanningRestoreRequestInput) =>
      apiFetch<SingleResponse<ApiPlanningRestoreRequest>>('/api/v1/snapshots/restore-requests', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    decideRestore: (
      id: string,
      body: { decision: 'approve' } | { decision: 'reject'; reason: string },
    ) =>
      apiFetch<SingleResponse<ApiPlanningRestoreRequest>>(
        `/api/v1/snapshots/restore-requests/${id}/decision`,
        { method: 'POST', body: JSON.stringify(body) },
      ).then((r) => r.data),
  },

  reports: {
    overtime: (year: number, month: number) =>
      apiFetch<{ data: ApiOvertimeRow[] }>(
        `/api/v1/reports/overtime?year=${year}&month=${month}`,
      ).then((r) => r.data),
    utilisation: (year: number, month: number, includeSubs = false) =>
      apiFetch<{ data: ApiUtilisationRow[] }>(
        `/api/v1/reports/utilisation?year=${year}&month=${month}${
          includeSubs ? '&includeSubs=true' : ''
        }`,
      ).then((r) => r.data),
    // GH #177 item 2: workforce-mix KPI.
    activeStaff: () =>
      apiFetch<{ data: ApiActiveStaffCounts }>('/api/v1/reports/active-staff').then((r) => r.data),
    // GH #177 item 7: operative vs subcontractor scheduled-hours mix.
    staffMix: (year: number, month: number) =>
      apiFetch<{ data: ApiStaffMix }>(`/api/v1/reports/staff-mix?year=${year}&month=${month}`).then(
        (r) => r.data,
      ),
    // GH #177 item 8: phase-lifecycle distribution (replaces utilisation-by-type).
    phaseLifecycle: (year: number, month: number) =>
      apiFetch<{ data: ApiPhaseLifecycleRow[] }>(
        `/api/v1/reports/phase-lifecycle?year=${year}&month=${month}`,
      ).then((r) => r.data),
    leave: (year: number, month?: number, staffId?: string) => {
      const q = new URLSearchParams({ year: String(year) });
      if (month) q.set('month', String(month));
      if (staffId) q.set('staffId', staffId);
      return apiFetch<{ data: ApiLeaveReportRow[] }>(`/api/v1/reports/leave?${q.toString()}`).then(
        (r) => r.data,
      );
    },
    consecutiveDays: () =>
      apiFetch<{ data: ApiConsecutiveDaysRow[] }>('/api/v1/reports/consecutive-days').then(
        (r) => r.data,
      ),
    subcontractors: (from: string, to: string) =>
      apiFetch<{ data: ApiSubcontractorRow[] }>(
        `/api/v1/reports/subcontractors?from=${from}&to=${to}`,
      ).then((r) => r.data),
    equipment: (from: string, to: string) =>
      apiFetch<{ data: ApiEquipmentDemandRow[] }>(
        `/api/v1/reports/equipment?from=${from}&to=${to}`,
      ).then((r) => r.data),
    // Phase 36-02 (Issue #37) mid-loop bundle: per-type drill-down feeding
    // the Reports page expandable row.
    equipmentTypeBookings: (typeId: string, from: string, to: string) =>
      apiFetch<{ data: ApiEquipmentTypeBookingRow[] }>(
        `/api/v1/reports/equipment/types/${typeId}/bookings?from=${from}&to=${to}`,
      ).then((r) => r.data),
    // GH #184 widget 1: skill demand across jobs/phases for the month.
    skills: (from: string, to: string) =>
      apiFetch<{ data: ApiSkillsDemandRow[] }>(`/api/v1/reports/skills?from=${from}&to=${to}`).then(
        (r) => r.data,
      ),
    // GH #184 widget 2: per-van booked/unavailable/free days + utilisation.
    vehicles: (from: string, to: string) =>
      apiFetch<{ data: ApiVehicleDemandRow[] }>(
        `/api/v1/reports/vehicles?from=${from}&to=${to}`,
      ).then((r) => r.data),
  },

  admin: {
    equipment: () =>
      apiFetch<ListResponse<ApiEquipmentItem>>('/api/v1/admin/equipment').then((r) => r.data),
    equipmentTypes: () =>
      apiFetch<ListResponse<ApiEquipmentType>>('/api/v1/admin/equipment/types').then((r) => r.data),
    createEquipmentType: (body: { name: string; description?: string }) =>
      apiFetch<SingleResponse<ApiEquipmentType>>('/api/v1/admin/equipment/types', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    updateEquipmentType: (id: string, body: { name?: string; description?: string }) =>
      apiFetch<SingleResponse<ApiEquipmentType>>(`/api/v1/admin/equipment/types/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    deleteEquipmentType: (id: string) =>
      apiFetch<void>(`/api/v1/admin/equipment/types/${id}`, { method: 'DELETE' }),
    createEquipmentItem: (body: {
      equipment_type_id: string;
      identifier: string;
      is_available?: boolean;
      notes?: string;
      // Phase 36-01 (Issue #37): null = N/A; 1..50 = explicit cap.
      quantity?: number | null;
    }) =>
      apiFetch<SingleResponse<ApiEquipmentItem>>('/api/v1/admin/equipment', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    // Phase 36-01 (Issue #37): bypasses apiFetch so we can intercept the
    // 409 body shape (Q3=b hard-block when reducing quantity below current
    // max allocation) and throw ReduceEquipmentQuantityConflictError with
    // the typed details payload. Other failures fall back to generic Error.
    updateEquipmentItem: async (
      id: string,
      body: {
        identifier?: string;
        is_available?: boolean;
        notes?: string;
        quantity?: number | null;
      },
    ): Promise<ApiEquipmentItem> => {
      // ARCH-4 (plan 50-09): goes through apiFetch (shared dev-user header +
      // 401 redirect + non-ok handling). The structured-quantity 409 is
      // recovered from the thrown ApiHttpError and remapped to the typed
      // ReduceEquipmentQuantityConflictError; any other 409 (no quantity
      // details) and other non-ok statuses propagate as the ApiHttpError.
      try {
        const body_ = await apiFetch<SingleResponse<ApiEquipmentItem>>(
          `/api/v1/admin/equipment/${id}`,
          { method: 'PATCH', body: JSON.stringify(body) },
        );
        return body_.data;
      } catch (err) {
        if (err instanceof ApiHttpError && err.status === 409) {
          const payload = (err.body ?? {}) as {
            error?: string;
            details?: ReduceEquipmentQuantityConflictDetails;
          };
          if (payload.details && typeof payload.details.current_max_allocation === 'number') {
            throw new ReduceEquipmentQuantityConflictError(
              payload.error ?? 'Cannot reduce equipment quantity',
              payload.details,
            );
          }
        }
        throw err;
      }
    },
    deleteEquipmentItem: (id: string) =>
      apiFetch<void>(`/api/v1/admin/equipment/${id}`, { method: 'DELETE' }),
    getConfig: () =>
      apiFetch<{ data: Record<string, unknown> }>('/api/v1/admin/config').then((r) => r.data),
    patchConfig: (updates: Record<string, unknown>) =>
      apiFetch<{ updated: string[] }>('/api/v1/admin/config', {
        method: 'PATCH',
        body: JSON.stringify(updates),
      }),
    // GH #130: Access Control editor (Superadmin-only).
    getRbacRoles: () =>
      apiFetch<SingleResponse<ApiRbacMatrix>>('/api/v1/admin/rbac-roles').then((r) => r.data),
    updateRbacRole: (role: string, permissions: string[]) =>
      apiFetch<SingleResponse<{ role: string; permissions: string[] }>>(
        `/api/v1/admin/rbac-roles/${role}`,
        { method: 'PATCH', body: JSON.stringify({ permissions }) },
      ).then((r) => r.data),
    getUsers: () =>
      apiFetch<
        ListResponse<{
          id: string;
          name: string;
          email: string;
          auth_role: string;
          can_clock_in: boolean;
          initials: string;
          is_subcontractor: boolean;
          is_active: boolean;
        }>
      >('/api/v1/admin/users').then((r) => r.data),
    updateUserRole: (id: string, role: string) =>
      apiFetch<
        SingleResponse<{
          id: string;
          name: string;
          email: string;
          auth_role: string;
          initials: string;
          is_active: boolean;
        }>
      >(`/api/v1/admin/users/${encodeURIComponent(id)}/role`, {
        method: 'PATCH',
        body: JSON.stringify({ role }),
      }).then((r) => r.data),
    // 14-05: orthogonal Time PWA capability toggle. Same superadmin gate as
    // updateUserRole on the backend; no self-edit guard (toggling your own
    // can_clock_in is harmless).
    updateUserCanClockIn: (id: string, can_clock_in: boolean) =>
      apiFetch<
        SingleResponse<{
          id: string;
          name: string;
          email: string;
          auth_role: string;
          can_clock_in: boolean;
          initials: string;
          is_active: boolean;
        }>
      >(`/api/v1/admin/users/${encodeURIComponent(id)}/can_clock_in`, {
        method: 'PATCH',
        body: JSON.stringify({ can_clock_in }),
      }).then((r) => r.data),
    audit: {
      list: (filters: ListAuditFilters = {}): Promise<ListAuditResponse> => {
        const q = new URLSearchParams();
        if (filters.date_from) q.set('date_from', filters.date_from);
        if (filters.date_to) q.set('date_to', filters.date_to);
        if (filters.source) q.set('source', filters.source);
        if (filters.actor_email) q.set('actor_email', filters.actor_email);
        if (filters.entity_type) q.set('entity_type', filters.entity_type);
        const qs = q.toString();
        return apiFetch<ListAuditResponse>(`/api/v1/admin/audit${qs ? `?${qs}` : ''}`);
      },
    },
  },

  /**
   * Plan 04-02: materials subsystem API client. Consumes Plan 04-01's endpoints
   * mounted at /api/v1/materials/*. Suppliers endpoint returns { suppliers }
   * shape (not the generic { data } envelope); catalog + extracted_items return
   * { rows }; confirm is wrapped in raw fetch to retain 409 body access for
   * distinct ALREADY_RESOLVED vs CATALOG_DUPLICATE_ACTIVE messaging.
   */
  materials: {
    // Phase 78-01 (§4): per-work-item allocation breakdown (product → sources →
    // lifecycle status). apiFetch does NOT auto-unwrap, so unwrap the { data }
    // envelope explicitly (see the api-client envelope memory).
    workItemAllocations: (workItemId: string) =>
      apiFetch<SingleResponse<ApiWorkItemAllocations>>(
        `/api/v1/materials/work-items/${workItemId}/allocations`,
      ).then((r) => r.data),

    // Phase 78-02 (Slice C): warehouse stock list — source for the panel's
    // reserve-from-stock picker. Unwraps the { data } envelope (api-client
    // envelope memory); apiFetch does NOT auto-unwrap.
    stock: () =>
      apiFetch<SingleResponse<ApiStockRow[]>>('/api/v1/materials/stock').then((r) => r.data),

    // Phase 78-02 (Slice C): planner reserves `qty` of a catalog row from
    // warehouse stock to a job/phase or ticket. Mirrors the LIVE Slice-C
    // contract POST /materials/work-items/:workItemId/allocations — CB supplies
    // the work-item/phase snapshots (target_kind + work_item_ion; phase_id +
    // phase_number for jobs, omitted for tickets), like #196. 409 when qty >
    // available — the panel surfaces it inline. Returns the unwrapped { id }.
    reserveAllocation: (
      workItemId: string,
      body: {
        target_kind: 'job' | 'ticket';
        work_item_ion?: string | null;
        phase_id?: string;
        phase_number?: number;
        catalog_row_id: string;
        qty: number;
      },
    ) =>
      apiFetch<SingleResponse<{ id: string }>>(
        `/api/v1/materials/work-items/${workItemId}/allocations`,
        { method: 'POST', body: JSON.stringify(body) },
      ).then((r) => r.data),

    // Phase 78-02 (Slice C): planner releases a not-yet-checked-out
    // reservation. 204 No Content; apiFetch returns undefined.
    releaseAllocation: (workItemId: string, allocationId: string) =>
      apiFetch<void>(`/api/v1/materials/work-items/${workItemId}/allocations/${allocationId}`, {
        method: 'DELETE',
      }),

    suppliers: () =>
      apiFetch<{ suppliers: ApiMaterialsSupplier[] }>('/api/v1/materials/suppliers').then(
        (r) => r.suppliers,
      ),

    catalog: (params: { supplier_id?: string; active?: boolean }) => {
      const qs = new URLSearchParams();
      if (params.supplier_id) qs.set('supplier_id', params.supplier_id);
      if (params.active !== undefined) qs.set('active', String(params.active));
      const q = qs.toString();
      return apiFetch<{ rows: ApiMaterialsCatalogRow[] }>(
        `/api/v1/materials/catalog${q ? `?${q}` : ''}`,
      ).then((r) => r.rows);
    },

    extractedItems: (params: {
      ingestion_job_id?: string;
      supplier_id?: string;
      status?: 'pending' | 'confirmed' | 'corrected' | 'rejected';
    }) => {
      const qs = new URLSearchParams();
      if (params.ingestion_job_id) qs.set('ingestion_job_id', params.ingestion_job_id);
      if (params.supplier_id) qs.set('supplier_id', params.supplier_id);
      if (params.status) qs.set('status', params.status);
      const q = qs.toString();
      return apiFetch<{ rows: ApiExtractedItem[] }>(
        `/api/v1/materials/extracted_items${q ? `?${q}` : ''}`,
      ).then((r) => r.rows);
    },

    corrections: (
      itemId: string,
      body: {
        field_name: string;
        old_value: string | null;
        new_value: string | null;
        reason?: string;
      },
    ) =>
      apiFetch<{ correction: ApiCorrection }>(
        `/api/v1/materials/extracted_items/${itemId}/corrections`,
        { method: 'POST', body: JSON.stringify(body) },
      ).then((r) => r.correction),

    /**
     * Confirm uses raw fetch (bypassing apiFetch) so the FE can read the 409
     * body and distinguish ALREADY_RESOLVED from CATALOG_DUPLICATE_ACTIVE for
     * the user-facing message. Other status codes (200/201/4xx/5xx) follow
     * the same throw-on-non-ok contract apiFetch provides.
     */
    confirm: async (
      itemId: string,
      body: {
        item_code: string;
        description: string;
        price: number;
        currency: string;
        unit: string;
        specs?: Record<string, unknown>;
      },
    ): Promise<{ extracted_item: ApiExtractedItem; catalog_row: ApiMaterialsCatalogRow }> => {
      // ARCH-4 (plan 50-09): previously hand-rolled fetch — which carried a
      // DUPLICATE copy of the 401→/login redirect that apiFetch already owns.
      // Now goes through apiFetch (single 401 handler + dev-user header +
      // non-ok handling); the 409 body is recovered from the thrown
      // ApiHttpError and remapped to the code-typed MaterialsConfirmConflictError.
      try {
        return await apiFetch<{
          extracted_item: ApiExtractedItem;
          catalog_row: ApiMaterialsCatalogRow;
        }>(`/api/v1/materials/extracted_items/${itemId}/confirm`, {
          method: 'POST',
          body: JSON.stringify(body),
        });
      } catch (err) {
        if (err instanceof ApiHttpError && err.status === 409) {
          const errBody = (err.body ?? {}) as { code?: string; error?: string };
          const code =
            errBody.code === 'CATALOG_DUPLICATE_ACTIVE'
              ? 'CATALOG_DUPLICATE_ACTIVE'
              : 'ALREADY_RESOLVED';
          throw new MaterialsConfirmConflictError(code, errBody.error ?? 'Conflict');
        }
        throw err;
      }
    },

    reject: (itemId: string, body: { reason?: string }) =>
      apiFetch<{ extracted_item: ApiExtractedItem }>(
        `/api/v1/materials/extracted_items/${itemId}/reject`,
        { method: 'POST', body: JSON.stringify(body) },
      ).then((r) => r.extracted_item),

    manual: (body: {
      supplier_id: string;
      item_code: string;
      description: string;
      price: number;
      currency: string;
      unit: string;
      specs?: Record<string, unknown>;
    }) =>
      apiFetch<{ row: ApiMaterialsCatalogRow }>('/api/v1/materials/catalog/manual', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.row),
  },

  subcontractors: {
    list: (params?: { includeInactive?: boolean }) => {
      const q = new URLSearchParams();
      if (params?.includeInactive) q.set('includeInactive', 'true');
      const qs = q.toString();
      return apiFetch<ListResponse<ApiStaff>>(
        qs ? `/api/v1/subcontractors?${qs}` : '/api/v1/subcontractors',
      ).then((r) => r.data);
    },
    create: (body: { company_id: string; email?: string }) =>
      apiFetch<SingleResponse<ApiStaff>>('/api/v1/subcontractors', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    update: (id: string, body: { email?: string; isActive?: boolean }) =>
      apiFetch<SingleResponse<ApiStaff>>(`/api/v1/subcontractors/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    delete: (id: string) => apiFetch<void>(`/api/v1/subcontractors/${id}`, { method: 'DELETE' }),
  },

  companies: {
    list: (type?: 'subcontractor') => {
      const q = new URLSearchParams();
      if (type) q.set('type', type);
      const qs = q.toString();
      return apiFetch<ListResponse<ApiCompany>>(
        qs ? `/api/v1/companies?${qs}` : '/api/v1/companies',
      ).then((r) => r.data);
    },
    create: (body: { name: string; initials?: string }) =>
      apiFetch<SingleResponse<ApiCompany>>('/api/v1/companies', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    update: (id: string, body: { name: string; initials?: string }) =>
      apiFetch<SingleResponse<ApiCompany>>(`/api/v1/companies/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    delete: (id: string) => apiFetch<void>(`/api/v1/companies/${id}`, { method: 'DELETE' }),
  },

  time: {
    entries: {
      list: (filters: ListTimeEntriesFilters = {}) => {
        const q = new URLSearchParams();
        if (filters.staff_id) q.set('staff_id', filters.staff_id);
        // Issue #174: append each operative id as a repeated staff_ids param.
        if (filters.staff_ids) {
          for (const id of filters.staff_ids) q.append('staff_ids', id);
        }
        if (filters.job_id) q.set('job_id', filters.job_id);
        if (filters.status) q.set('status', filters.status);
        if (filters.approval_status) q.set('approval_status', filters.approval_status);
        if (filters.clock_in_geofence_status)
          q.set('clock_in_geofence_status', filters.clock_in_geofence_status);
        if (filters.date_from) q.set('date_from', filters.date_from);
        if (filters.date_to) q.set('date_to', filters.date_to);
        const qs = q.toString();
        return apiFetch<ListTimeEntriesResponse>(
          qs ? `/api/v1/time/entries?${qs}` : '/api/v1/time/entries',
        );
      },
      patch: (id: string, body: PatchTimeEntryBody) =>
        apiFetch<SingleResponse<ApiTimeEntry>>(`/api/v1/time/entries/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        }).then((r) => r.data),
      // Phase 35-01 (Issue #38): admin-only hard delete with required reason
      // (reason captured in audit_events; row state also captured in audit_log
      // via the audit_trigger_fn on time_entries).
      delete: (id: string, body: { reason: string }) =>
        apiFetch<void>(`/api/v1/time/entries/${id}`, {
          method: 'DELETE',
          body: JSON.stringify(body),
        }),
      // GH #185: per-entry approval workflow (DISTINCT from edit-requests).
      // submit = Planner draft→pending; approve/reject = Admin/SuperAdmin.
      // Reject requires a mandatory free-form reason.
      submitForApproval: (id: string) =>
        apiFetch<SingleResponse<ApiTimeEntry>>(`/api/v1/time/entries/${id}/submit`, {
          method: 'POST',
        }).then((r) => r.data),
      approve: (id: string) =>
        apiFetch<SingleResponse<ApiTimeEntry>>(`/api/v1/time/entries/${id}/approve`, {
          method: 'POST',
        }).then((r) => r.data),
      reject: (id: string, reason: string) =>
        apiFetch<SingleResponse<ApiTimeEntry>>(`/api/v1/time/entries/${id}/reject`, {
          method: 'POST',
          body: JSON.stringify({ reason }),
        }).then((r) => r.data),
    },
    editRequests: {
      list: (filters: ListEditRequestsFilters = {}) => {
        const q = new URLSearchParams();
        if (filters.status) q.set('status', filters.status);
        if (filters.requested_by_staff_id)
          q.set('requested_by_staff_id', filters.requested_by_staff_id);
        if (filters.time_entry_id) q.set('time_entry_id', filters.time_entry_id);
        if (filters.date_from) q.set('date_from', filters.date_from);
        if (filters.date_to) q.set('date_to', filters.date_to);
        const qs = q.toString();
        return apiFetch<ListEditRequestsResponse>(
          qs ? `/api/v1/time/edit-requests?${qs}` : '/api/v1/time/edit-requests',
        );
      },
      review: (id: string, body: ReviewEditRequestBody) =>
        apiFetch<SingleResponse<ReviewEditRequestResult>>(`/api/v1/time/edit-requests/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        }).then((r) => r.data),
    },
    // Phase 79-02: auto-capped clock-outs awaiting pre-payroll review (79-01).
    issues: {
      list: () =>
        apiFetch<SingleResponse<ApiTimeIssue[]>>('/api/v1/admin/time/issues').then((r) => r.data),
      review: (id: string) =>
        apiFetch<SingleResponse<{ reviewed: true }>>(`/api/v1/admin/time/issues/${id}/review`, {
          method: 'POST',
        }).then((r) => r.data),
    },
    // Open no-shows: scheduled segments the worker never clocked into. Sibling
    // of `issues` (auto-cap review); a component may merge the two sources.
    noShows: {
      list: () =>
        apiFetch<SingleResponse<ApiNoShow[]>>('/api/v1/admin/time/no-shows').then((r) => r.data),
      // Resolve an open no-show with a categorised absence reason.
      resolve: (segmentId: string, body: { reason: AbsenceReason; note?: string | null }) =>
        apiFetch<SingleResponse<{ ok: true }>>(`/api/v1/admin/time/no-shows/${segmentId}/resolve`, {
          method: 'POST',
          body: JSON.stringify(body),
        }).then(() => undefined),
      // Re-categorise (or, with reason: null, clear) an already-resolved no-show.
      recategorise: (
        segmentId: string,
        body: { reason: AbsenceReason | null; note?: string | null },
      ) =>
        apiFetch<SingleResponse<{ ok: true }>>(`/api/v1/admin/time/no-shows/${segmentId}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        }).then(() => undefined),
      // Back-fill a real time entry for a no-show (worked but never clocked in).
      createEntry: (
        segmentId: string,
        body: { clockInTs: string; clockOutTs: string; note?: string | null },
      ) =>
        apiFetch<SingleResponse<{ id: string }>>(`/api/v1/admin/time/no-shows/${segmentId}/entry`, {
          method: 'POST',
          body: JSON.stringify(body),
        }).then((r) => r.data),
    },
    // Pending operative self-declarations (a worker flagged a missed/interrupted
    // shift). Sibling of `noShows`; resolved through the same segment-keyed
    // resolve/createEntry endpoints, which auto-mark the declaration actioned.
    declarations: {
      list: () =>
        apiFetch<SingleResponse<ApiDeclaration[]>>('/api/v1/admin/time/declarations').then(
          (r) => r.data,
        ),
    },
    exports: {
      createBatch: (body: CreatePayrollExportBody) =>
        apiFetch<SingleResponse<PayrollExport>>('/api/v1/time/export/batches', {
          method: 'POST',
          body: JSON.stringify(body),
        }).then((r) => r.data),
      listBatches: (limit = 25, offset = 0) =>
        apiFetch<PayrollExportList>(
          `/api/v1/time/export/batches?${new URLSearchParams({ limit: String(limit), offset: String(offset) })}`,
        ),
      getBatch: (id: string) =>
        apiFetch<SingleResponse<PayrollExport>>(
          `/api/v1/time/export/batches/${encodeURIComponent(id)}`,
        ).then((r) => r.data),
      batchCsvUrl: (id: string): string => {
        const url = `${BASE_URL}/api/v1/time/export/batches/${encodeURIComponent(id)}/csv`;
        // Download anchors cannot carry the dev-user header used by apiFetch.
        return import.meta.env.DEV || import.meta.env.VITE_DEV_USER
          ? `${url}?${new URLSearchParams({ dev_user: DEV_USER })}`
          : url;
      },
      deliverBatchSheet: (id: string) =>
        apiFetch<SingleResponse<ExportSheetResult>>(
          `/api/v1/time/export/batches/${encodeURIComponent(id)}/sheets`,
          { method: 'POST' },
        ).then((r) => r.data),
    },
  },

  notifications: {
    list: (params?: { limit?: number; unreadOnly?: boolean }) => {
      const q = new URLSearchParams();
      if (params?.limit) q.set('limit', String(params.limit));
      if (params?.unreadOnly !== undefined) q.set('unreadOnly', String(params.unreadOnly));
      const qs = q.toString();
      return apiFetch<{ data: AppNotification[]; unreadCount: number }>(
        qs ? `/api/v1/notifications?${qs}` : '/api/v1/notifications',
      );
    },
    markRead: (id: string) =>
      apiFetch<{ data: AppNotification }>(`/api/v1/notifications/${id}/read`, {
        method: 'PATCH',
      }),
    markAllRead: () =>
      apiFetch<{ data: { updated: number } }>('/api/v1/notifications/read-all', {
        method: 'POST',
      }),
  },
};
